<template>
  <section class="workspace floorplan-workspace">
    <div class="composer-card">
      <div class="composer-head">
        <div>
          <span class="step-label">AI创作</span>
          <h2>平面图锁定户型，参考图只提取装修风格</h2>
        </div>
        <button class="text-button" @click="resetForm">清空设置</button>
      </div>

      <div class="composer-body">
        <div class="floorplan-upload-column">
          <label class="upload-box floorplan-upload-box" :class="{ 'has-file': previewUrl }">
            <input type="file" accept="image/*" @change="handleFile" />
            <img v-if="previewUrl" :src="$ossImageUrl(previewUrl, 'preview')" alt="户型图预览" decoding="async" />
            <div v-else class="upload-content">
              <div class="upload-icon">
                <svg viewBox="0 0 24 24"><path d="M12 16V5m0 0L8 9m4-4 4 4"/><path d="M5 15v4h14v-4"/></svg>
              </div>
              <strong>上传平面图</strong>
              <span>上传后直接在图上框选本次要生成的空间</span>
            </div>
            <button v-if="previewUrl" type="button" class="replace-file">更换图片</button>
          </label>

          <div v-if="previewUrl" class="plan-constraint-summary" :class="{ ready: floorPlanMarked }">
            <div class="constraint-summary-main">
              <span class="constraint-state-icon">
                <i :class="floorPlanMarked ? 'el-icon-check' : 'el-icon-edit-outline'"></i>
              </span>
              <div>
                <strong>{{ floorPlanMarked ? `${selectedSpace}区域已标记` : '还未标记目标空间' }}</strong>
                <small>{{ floorPlanMarked ? selectedDirectionText : '请框出房间，并选择镜头朝向' }}</small>
              </div>
            </div>
            <button type="button" @click="openFloorPlanMarking">
              {{ floorPlanMarked ? '重新标记' : '在图上标记' }}
            </button>
          </div>
        </div>

        <div class="form-panel">
          <div class="field-group reference-field">
            <div class="reference-toolbar">
              <div>
                <div class="field-title"><span>01</span> 风格参考图 <em>必填</em></div>
                <p>至少 1 张、最多 3 张；AI 写提示词时会与平面图一起分析，生成效果图前转成风格文字</p>
              </div>
              <span v-if="referenceItems.length" class="reference-count">
                {{ referenceItems.length }} / 3
              </span>
            </div>

            <label v-if="!referenceItems.length" class="reference-empty">
              <input type="file" accept="image/*" multiple @change="handleReferenceFiles" />
              <span class="reference-empty-icon">
                <i class="el-icon-picture-outline" aria-hidden="true"></i>
                <i class="el-icon-plus" aria-hidden="true"></i>
              </span>
              <span class="reference-empty-copy">
                <strong>添加参考图</strong>
                <small>建议上传 1–3 张风格一致的图片</small>
              </span>
              <i class="el-icon-upload2 reference-upload-icon" aria-hidden="true"></i>
            </label>

            <div v-else class="reference-grid">
              <article v-for="item in referenceItems" :key="item.id" class="reference-card">
                <div class="reference-preview">
                  <img :src="$ossImageUrl(item.previewUrl, 'thumbnail')" :alt="`${item.space}参考图`" loading="lazy" decoding="async" />
                  <button type="button" title="删除参考图" aria-label="删除参考图" @click="removeReferenceImage(item)">
                    <i class="el-icon-delete"></i>
                  </button>
                </div>
                <div class="reference-card-footer">
                  <small>应用空间</small>
                  <el-select v-model="item.space" size="mini" aria-label="选择参考图适用空间">
                    <el-option v-for="space in referenceSpaces" :key="space" :label="space" :value="space" />
                  </el-select>
                </div>
              </article>

              <label v-if="referenceItems.length < 3" class="reference-add-card">
                <input type="file" accept="image/*" multiple @change="handleReferenceFiles" />
                <span><i class="el-icon-plus" aria-hidden="true"></i></span>
                <strong>继续添加</strong>
                <small>还可添加 {{ 3 - referenceItems.length }} 张</small>
              </label>
            </div>

            <div class="reference-status" :class="{ active: applicableReferenceItems.length }">
              <i :class="applicableReferenceItems.length ? 'el-icon-circle-check' : 'el-icon-info'"></i>
              <span v-if="applicableReferenceItems.length">
                将从 {{ applicableReferenceItems.length }} 张图提取风格文字，不会把参考图户型传给效果图模型
              </span>
              <span v-else-if="referenceItems.length">
                暂无适用于“{{ selectedSpace }}”的参考图，可改为空间专属或全屋通用
              </span>
              <span v-else>必须上传至少 1 张适用于当前空间的参考图</span>
            </div>
          </div>

          <div class="field-group">
            <div class="field-title"><span>02</span> 选择空间 <em>必填</em></div>
            <div class="space-groups">
              <div v-for="group in spaceGroups" :key="group.label" class="space-group">
                <span class="space-group-label">{{ group.label }}</span>
                <div class="chips">
                  <button
                    v-for="space in group.options"
                    :key="space"
                    :class="{ selected: selectedSpace === space }"
                    @click="selectSpace(space)"
                  >
                    {{ space }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="field-group">
            <div class="field-title"><span>03</span> 装修风格 <em>必填</em></div>
            <div class="chips">
              <button
                v-for="style in styles"
                :key="style"
                :class="{ selected: selectedStyle === style }"
                @click="selectedStyle = style"
              >
                {{ style }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="ai-prompt-panel">
        <div class="prompt-heading">
          <div class="ai-avatar">AI</div>
          <div>
            <strong>效果图风格要求 <em class="required-label">必填</em></strong>
            <span>AI 会综合平面图、参考图、所选空间和装修风格撰写，户型结构仍以平面图为准</span>
          </div>
          <em>{{ prompt.length }}/500</em>
        </div>
        <div class="prompt-row">
          <textarea
            v-model="prompt"
            :readonly="writingPrompt || enhancingPrompt"
            maxlength="500"
            placeholder="例如：奶油色墙面、浅色木饰面、暖色无主灯、家具线条简洁"
          ></textarea>
          <div class="prompt-actions" aria-label="AI 提示词工具">
            <div class="prompt-action">
              <button
                class="magic-button write-button"
                :class="{ loading: writingPrompt }"
                :disabled="writingPrompt || enhancingPrompt"
                @click="writePrompt"
              >
                <span v-if="writingPrompt" class="magic-spinner"></span>
                <i v-else class="el-icon-magic-stick" aria-hidden="true"></i>
                <span>{{ writingPrompt ? 'AI 写作中' : 'AI 帮我写' }}</span>
              </button>
            </div>
            <div class="prompt-action">
              <button
                class="magic-button enhance-button"
                :class="{ loading: enhancingPrompt }"
                :disabled="writingPrompt || enhancingPrompt"
                @click="enhancePrompt"
              >
                <span v-if="enhancingPrompt" class="magic-spinner"></span>
                <i v-else class="el-icon-edit-outline" aria-hidden="true"></i>
                <span>{{ enhancingPrompt ? 'AI 完善中' : '帮我完善' }}</span>
              </button>
            </div>
          </div>
        </div>
        <div class="prompt-suggestions">
          <span>快捷描述</span>
          <button v-for="item in quickPrompts" :key="item" @click="addQuickPrompt(item)">+ {{ item }}</button>
          <button class="preset-toggle" :class="{ active: promptPresetVisible }" @click="togglePromptPresets">
            <i class="el-icon-view"></i>
            {{ promptPresetVisible ? '收起系统预设' : '查看 AI 系统预设' }}
          </button>
        </div>
        <transition name="preset-reveal">
          <div v-if="promptPresetVisible" class="prompt-presets">
            <div class="preset-intro">
              <div>
                <strong>当前实际使用的 AI 提示词</strong>
                <span>内容直接读取自后端配置，和按钮调用的预设保持一致。</span>
              </div>
              <span class="preset-live"><i></i> 实时配置</span>
            </div>
            <div v-if="promptPresetsLoading" class="preset-state"><span class="magic-spinner"></span> 正在读取系统预设…</div>
            <div v-else-if="promptPresetsError" class="preset-state preset-error">
              {{ promptPresetsError }}
              <button @click="loadPromptPresets">重新读取</button>
            </div>
            <div v-else class="preset-grid">
              <article v-for="item in promptPresetItems" :key="item.key" class="preset-card">
                <header>
                  <span>{{ item.order }}</span>
                   <div><strong>{{ formatUserFacingBrand(item.title) }}</strong><small>{{ formatUserFacingBrand(item.description) }}</small></div>
                  <em>{{ formatModelName(item.model) }}</em>
                </header>
                <div class="preset-block">
                  <label>系统提示词（System Prompt）</label>
                  <pre>{{ formatUserFacingBrand(item.systemPrompt) }}</pre>
                </div>
              </article>
            </div>
          </div>
        </transition>
      </div>

      <div class="constraint-checkbar">
        <span :class="{ done: Boolean(floorPlanFile) }"><i>{{ floorPlanFile ? '✓' : '1' }}</i> 上传户型图</span>
        <b></b>
        <span :class="{ done: floorPlanMarked }"><i>{{ floorPlanMarked ? '✓' : '2' }}</i> 框选空间与视角</span>
        <b></b>
        <span :class="{ done: applicableReferenceItems.length > 0 }"><i>{{ applicableReferenceItems.length ? '✓' : '3' }}</i> 添加参考图</span>
        <b></b>
        <span :class="{ done: Boolean(selectedSpace && selectedStyle) }"><i>{{ selectedSpace && selectedStyle ? '✓' : '4' }}</i> 选择空间与风格</span>
        <b></b>
        <span :class="{ done: Boolean(prompt.trim()) }"><i>{{ prompt.trim() ? '✓' : '5' }}</i> 填写风格要求</span>
      </div>

      <div class="composer-footer">
        <div class="settings-row">
          <button class="setting-pill" type="button" disabled><span class="dot ai-dot"></span> FastIMG</button>
          <AspectRatioSelector
            v-model="selectedRatio"
            compact
            :disabled="generating"
            :options="ratioOptions"
            aria-label="选择场景尺寸比例"
          />
          <button class="setting-pill"><span class="grid-icon">▦</span> 1 张效果图</button>
          <ResolutionSelector v-model="selectedResolution" compact :disabled="generating" />
          <QualitySelector v-model="selectedQuality" compact :disabled="generating" />
          <button class="setting-pill constraint-pill"><i class="el-icon-lock"></i> 参考图仅转风格文字</button>
        </div>
        <button class="generate-button" :class="{ loading: generating }" :disabled="generating" @click="startGenerate">
          <span v-if="generating" class="spinner"></span>
          <span v-else>✦</span>
          {{ generating ? generationStage + ' ' + progress + '%' : '生成 1 张户型效果图' }}
          <small v-if="!generating">效果图模型只接收平面图约束</small>
        </button>
      </div>
      <div v-if="generating" class="progress-track"><i :style="{ width: progress + '%' }"></i></div>
    </div>

    <section class="history-section">
      <div class="section-head">
        <div>
          <h2>AI创作记录</h2>
          <p>这里只展示通过 AI 创作生成的方案</p>
        </div>
        <div class="filter-tabs"><button class="active">全部</button><button>生成中</button><button>已完成</button></div>
      </div>

      <div v-for="record in records" :key="record.id" class="record-card" :class="`record-${record.status}`">
        <div class="record-meta">
          <div class="record-title">
            <span class="status-dot" :class="`dot-${record.status}`"></span>
            <strong>{{ record.space }} · {{ record.style }}</strong>
            <span class="stage-tag" :class="`stage-${record.stage}`">{{ stageText(record.stage) }}</span>
            <span class="ratio-tag"><i :style="ratioFrameStyle(record.size, 12, 9)"></i>{{ record.size }}</span>
            <span class="success-tag" :class="`tag-${record.status}`">{{ statusText(record.status) }}</span>
          </div>
          <div class="record-meta-actions">
            <span>
              <b>生成时间</b>
              <time class="creation-date" :datetime="record.createdAt || undefined">{{ formatRecordTime(record.createdAt) }}</time>
              · 已展示 {{ completedImageCount(record) }}/{{ recordSlotCount(record) }} 张
            </span>
            <button
              type="button"
              class="record-delete-button"
              :disabled="deletingRecordIds.includes(record.id)"
              title="删除创作记录"
              aria-label="删除创作记录"
              @click="removeRecord(record)"
            >
              <i :class="deletingRecordIds.includes(record.id) ? 'el-icon-loading' : 'el-icon-delete'"></i>
            </button>
          </div>
        </div>

        <GenerationModelDetails :record="record" />

        <div v-if="record.taskIds.length || record.images.some(Boolean)" class="image-grid two-up">
          <article
            v-for="index in recordSlotCount(record)"
            :key="`${record.id}-${index}`"
            class="result-card"
            :class="{
              'loading-result': !imageAt(record, index - 1) && taskStatus(record, index - 1) !== 'failed',
              'failed-slot': !imageAt(record, index - 1) && taskStatus(record, index - 1) === 'failed'
            }"
          >
            <div
              v-if="imageAt(record, index - 1)"
              class="result-image"
              :style="{ backgroundImage: `url(${$ossImageUrl(imageAt(record, index - 1), 'thumbnail')})` }"
              role="button"
              tabindex="0"
              @click="openImageViewer(imageAt(record, index - 1), record, index - 1)"
              @keydown.enter="openImageViewer(imageAt(record, index - 1), record, index - 1)"
            >
              <span v-if="index === 1" class="choice-badge">推荐</span>
              <div class="image-actions">
                <button title="查看大图" @click.stop="openImageViewer(imageAt(record, index - 1), record, index - 1)">⌕</button>
                <button title="下载" @click.stop="downloadImage(imageAt(record, index - 1), record, index - 1)">↓</button>
              </div>
            </div>
            <div v-else-if="taskStatus(record, index - 1) === 'failed'" class="failed-visual">
              <span>!</span>
              <strong>方案 {{ String(index).padStart(2, '0') }} 未完成</strong>
              <small>{{ taskErrorMessage(record, index - 1) }}</small>
            </div>
            <div v-else class="loading-visual">
              <span class="loading-spinner"></span>
              <strong>方案 {{ String(index).padStart(2, '0') }}</strong>
              <small>{{ taskProgress(record, index - 1) }}</small>
            </div>
            <ImageDimensionsRow :items="[{ label: '图片尺寸', url: imageAt(record, index - 1), width: record.images && record.images[index - 1] ? record.images[index - 1].width : null, height: record.images && record.images[index - 1] ? record.images[index - 1].height : null }]" />
            <div v-if="imageAt(record, index - 1)" class="result-caption">
              <span>方案 {{ String(index).padStart(2, '0') }}</span>
              <button @click="openRefineDialog(record, index - 1)">
                {{ record.stage === 'structure' ? '选定户型并套用风格' : '继续精修' }}
              </button>
              <WorkflowTuningButton
                variant="button"
                :image-url="imageAt(record, index - 1)"
                :prompt="record.prompt || ''"
                :source-model="record.model || ''"
                module-name="平面转效果图"
                label="工作流"
              />
            </div>
            <div v-else class="result-caption">
              <span>{{ taskStatus(record, index - 1) === 'failed' ? '本张未完成' : 'AI 正在绘制' }}</span>
              <em>{{ taskProgress(record, index - 1) }}</em>
            </div>
          </article>
        </div>

        <div v-else class="failed-result">
          <span>!</span>
          <div><strong>本次生成未完成</strong><p>{{ record.error || '生成服务暂时不可用，请重新尝试' }}</p></div>
        </div>

        <div v-if="record.status === 'failed' && recordErrorDetails(record)" class="record-error-guidance">
          <i class="el-icon-warning-outline"></i>
          <div>
            <strong>失败原因：{{ recordErrorDetails(record).message }}</strong>
            <span>处理建议：{{ recordErrorDetails(record).suggestion }}</span>
          </div>
          <button v-if="recordErrorDetails(record).retryable" :disabled="generating" @click="retryRecord(record)">
            重新生成
          </button>
        </div>

        <section v-if="hasRecordInputContext(record)" class="record-inputs">
          <div class="record-inputs-head">
            <strong>本次输入</strong>
            <span>本次上传的户型图与参考图</span>
          </div>
          <div class="record-input-groups">
            <div v-if="uploadedFloorPlanImages(record).length" class="record-input-group">
              <span>户型图</span>
              <div class="record-input-images">
                <button
                  v-for="image in uploadedFloorPlanImages(record)"
                  :key="`${record.id}-${image.type}-${image.url}`"
                  type="button"
                  :title="`查看${image.label}`"
                  @click="openRecordInputImage(image, record)"
                >
                  <img :src="$ossImageUrl(image.url, 'thumbnail')" :alt="image.label" loading="lazy" decoding="async" />
                  <small>{{ image.label }}</small>
                </button>
              </div>
            </div>
            <div v-if="record.inputContext.referenceImages.length" class="record-input-group">
              <span>参考图</span>
              <div class="record-input-images">
                <button
                  v-for="image in record.inputContext.referenceImages"
                  :key="`${record.id}-${image.label}-${image.url}`"
                  type="button"
                  :title="`查看${image.label}`"
                  @click="openRecordInputImage(image, record)"
                >
                  <img :src="$ossImageUrl(image.url, 'thumbnail')" :alt="image.label" loading="lazy" decoding="async" />
                  <small>{{ image.label }}</small>
                </button>
              </div>
            </div>
          </div>
        </section>
        <div v-else class="record-inputs-unavailable">
          <i class="el-icon-info"></i> 此历史记录生成时尚未保存上传图片
        </div>

        <div class="record-footer">
          <p class="record-prompt" :title="record.prompt || '保持户型结构，空间通透明亮'">
            <span>“</span>{{ record.prompt || '保持户型结构，空间通透明亮' }}<span>”</span>
          </p>
          <div v-if="record.status === 'completed'"><button>重新生成</button><button class="primary-outline">导出方案</button></div>
          <div v-else-if="record.status === 'generating'" class="polling-hint">
            <i></i> 已展示 {{ completedImageCount(record) }} 张，剩余图片继续生成
          </div>
        </div>
      </div>

      <div v-if="!records.length" class="empty-record">
        <div class="empty-icon"><i v-if="recordsLoading" class="el-icon-loading"></i><template v-else>✦</template></div>
        <strong>{{ recordsLoading ? '正在加载 AI 创作记录' : '还没有 AI 创作记录' }}</strong>
        <span>{{ recordsLoading ? '正在获取历史方案，请稍候…' : '上传户型图并完成区域标记后，方案会展示在这里' }}</span>
      </div>
      <AppPagination :page="recordsPage" :page-size="12" :total="recordsTotal" :loading="recordsLoading" @change="changeRecordsPage" />
    </section>

    <el-dialog
      title="在户型图上标记本次生成区域"
      :visible.sync="markingDialogVisible"
      custom-class="floorplan-marking-dialog"
      width="920px"
      :close-on-click-modal="false"
      append-to-body
    >
      <div class="marking-layout">
        <div class="marking-board">
          <div class="marking-board-toolbar">
            <div>
              <strong>按住鼠标拖动，框出“{{ selectedSpace }}”</strong>
              <span>{{ selectionGuidance }}</span>
            </div>
            <button type="button" @click="clearSelection"><i class="el-icon-refresh-left"></i> 重画</button>
          </div>
          <div class="marking-canvas-shell">
            <div
              ref="markingStage"
              class="marking-image-wrap"
              @pointerdown="startSelection"
              @pointermove="moveSelection"
              @pointerup="endSelection"
              @pointercancel="endSelection"
            >
              <img :src="$ossImageUrl(previewUrl, 'large')" alt="待标记户型图" draggable="false" decoding="async" />
              <div v-if="selectionValid" class="room-selection" :style="selectionStyle">
                <span>{{ selectedSpace }}</span>
                <i class="selection-arrow" :style="directionArrowStyle">➜</i>
              </div>
            </div>
          </div>
        </div>

        <aside class="marking-settings">
          <div class="marking-step">
            <span>01</span>
            <div><strong>目标空间</strong><small>与创作台选择保持一致</small></div>
          </div>
          <div class="marking-space-name">{{ selectedSpace }}</div>

          <div class="marking-step direction-step">
            <span>02</span>
            <div><strong>镜头朝向</strong><small>箭头表示效果图视线方向</small></div>
          </div>
          <div class="direction-grid">
            <button
              v-for="direction in viewDirections"
              :key="direction.value"
              type="button"
              :class="{ selected: selectedDirection === direction.value }"
              @click="selectedDirection = direction.value"
            >
              <i :style="{ transform: `rotate(${direction.rotation}deg)` }">➜</i>
              <span>{{ direction.shortLabel }}</span>
            </button>
          </div>

          <label class="viewpoint-note">
            <span>补充镜头说明 <em>可选</em></span>
            <input v-model="viewpointNote" maxlength="40" placeholder="例如：从阳台看向餐厅" />
          </label>

          <label class="viewpoint-note layout-note">
            <span>局部关系说明 <em>建议填写</em></span>
            <textarea
              v-model="localLayoutNote"
              maxlength="120"
              placeholder="例如：前方餐厅，左侧书房和书柜，右侧电视墙，后方阳台"
            ></textarea>
          </label>

          <div class="marking-rule">
            <i class="el-icon-lock"></i>
            <p>效果图模型只接收干净完整户型图和两张无标记区域裁片；框线与箭头不会写入图片。</p>
          </div>
        </aside>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="markingDialogVisible = false">稍后标记</el-button>
        <el-button type="primary" :disabled="!selectionValid || !selectedDirection" @click="confirmFloorPlanMarking">
          确认区域与视角
        </el-button>
      </span>
    </el-dialog>

    <el-dialog
      :title="refineDialogTitle"
      :visible.sync="refineDialogVisible"
      custom-class="refine-dialog"
      width="720px"
      :close-on-click-modal="false"
      append-to-body
    >
      <div class="refine-workspace">
        <div class="refine-preview">
          <img v-if="refineDraft.image" :src="$ossImageUrl(refineDraft.image, 'preview')" :alt="refineMode === 'style' ? '选中的户型结构稿' : '选中的效果图'" decoding="async" />
          <span>{{ refineDraft.space }} · {{ refineDraft.style }} · 方案 {{ String(refineDraft.index + 1).padStart(2, '0') }}</span>
        </div>
        <div class="refine-form">
          <div class="refine-kicker">{{ refineMode === 'style' ? '已锁定户型结构稿' : '已锁定效果图' }}</div>
          <h3>{{ refineMode === 'style' ? '只为当前结构套用风格' : '补充这一次要加强的细节' }}</h3>
          <textarea
            v-model="refinePrompt"
            maxlength="300"
            :placeholder="refineMode === 'style'
              ? '例如：奶油色墙面、浅色木饰面、暖色无主灯、家具线条简洁'
              : '例如：加强木饰面纹理，优化沙发与茶几比例，灯光更柔和'"
          ></textarea>
          <AiPromptOptimizer
            v-model="refinePrompt"
            :disabled="refining"
            :max-length="300"
            :context="refineMode === 'style' ? '为锁定的户型结构稿补充装修风格要求' : '对室内效果图进行细节精修的要求'"
          />
          <div class="refine-locks">
            <span><i class="el-icon-lock"></i> 构图与镜头</span>
            <span><i class="el-icon-lock"></i> 空间与布局</span>
            <span><i class="el-icon-magic-stick"></i> {{ refineMode === 'style' ? '参考图只套风格' : '材质与灯光' }}</span>
          </div>
          <div v-if="refineMode === 'style'" class="refine-reference-note">
            <i class="el-icon-picture-outline"></i>
            将使用当前 {{ applicableReferenceItems.length }} 张风格参考图，并重新附带完整户型图校验结构
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button :disabled="refining" @click="refineDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="refining" @click="startRefinement">
          {{ refineMode === 'style' ? '套用风格生成 1 张' : '生成 1 张精细版' }}
        </el-button>
      </span>
    </el-dialog>

    <transition name="viewer">
      <div
        v-if="imageViewer.open"
        ref="imageViewer"
        class="image-viewer"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
        :aria-label="imageViewer.title"
        @click.self="closeImageViewer"
        @keydown.esc="closeImageViewer"
      >
        <div class="viewer-topbar">
          <div><strong>{{ imageViewer.title }}</strong><span>滚轮或按钮缩放，按住图片可拖动</span></div>
          <button class="viewer-close" title="关闭大图" @click="closeImageViewer">×</button>
        </div>
        <div
          ref="viewerStage"
          class="viewer-stage"
          :class="{ dragging: viewerDragging }"
          @wheel.prevent="handleViewerWheel"
          @pointerdown="startImageDrag"
          @pointermove="moveImageDrag"
          @pointerup="endImageDrag"
          @pointercancel="endImageDrag"
        >
          <img :src="$ossImageUrl(imageViewer.src, 'large')" :alt="imageViewer.title" :style="{ width: `${viewerScale * 100}%` }" draggable="false" />
        </div>
        <div class="viewer-toolbar">
          <button :disabled="viewerScale <= 0.5" @click="zoomImage(-0.25)">−</button>
          <span>{{ Math.round(viewerScale * 100) }}%</span>
          <button :disabled="viewerScale >= 4" @click="zoomImage(0.25)">+</button>
          <button class="viewer-reset" @click="resetImageZoom">1:1</button>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
/* 平面图锁定墙体/门窗/目标区域，参考图只提供装修风格；选区、镜头和比例共同进入请求。 */
import { mergePendingGenerations, removePendingGeneration, savePendingGeneration } from '@/utils/pendingGenerationRecords'
import AspectRatioSelector from '@/components/AspectRatioSelector.vue'
import ResolutionSelector from '@/components/ResolutionSelector.vue'
import QualitySelector from '@/components/QualitySelector.vue'
import ImageDimensionsRow from '@/components/ImageDimensionsRow.vue'
import AiPromptOptimizer from '@/components/AiPromptOptimizer.vue'
import { DEFAULT_GENERATION_QUALITY, MINIMUM_OUTPUT_RESOLUTION } from '@/utils/outputQuality'
import { closestImageRatio, SUPPORTED_IMAGE_RATIOS } from '@/utils/imageAspectRatio'
import { selectionCoordinateText } from '@/utils/selectionGeometry'
import { formatModelName, formatUserFacingBrand } from '@/utils/modelDisplay'

const FLOORPLAN_MODE_MARKER = '【户型约束创作】'
const SPACE_GROUPS = [
  {
    label: '住宅常用',
    options: [
      '全屋', '客厅', '客餐厅', '餐厅', '玄关', '主卧', '次卧', '儿童房',
      '老人房', '客房', '书房', '厨房', '卫生间', '阳台', '衣帽间'
    ]
  }
]
const SPACE_OPTIONS = SPACE_GROUPS.reduce((result, group) => result.concat(group.options), [])
const RATIO_OPTIONS = SUPPORTED_IMAGE_RATIOS

export default {
  name: 'FloorPlanCreation',
  components: { AspectRatioSelector, ResolutionSelector, QualitySelector, ImageDimensionsRow, AiPromptOptimizer },
  props: {
    preset: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      spaceGroups: SPACE_GROUPS,
      spaces: SPACE_OPTIONS,
      styles: [
        '现代简约', '奶油风', '原木风', '轻奢风', '新中式', '极简风',
        '日式风', '北欧风', '法式风', '美式风', '中古风', '侘寂风',
        '工业风', '意式风', '复古风', '南洋风', '混搭风'
      ],
      quickPrompts: ['暖色无主灯', '增加收纳', '保留自然采光', '浅色木地板'],
      selectedSpace: '客餐厅',
      selectedStyle: '现代简约',
      ratioOptions: RATIO_OPTIONS,
      selectedRatio: '16:9',
      selectedResolution: MINIMUM_OUTPUT_RESOLUTION,
      selectedQuality: DEFAULT_GENERATION_QUALITY,
      prompt: '',
      writingPrompt: false,
      enhancingPrompt: false,
      promptPresetVisible: false,
      promptPresetsLoading: false,
      promptPresetsError: '',
      promptPresets: null,
      previewUrl: '',
      floorPlanFile: null,
      floorPlanMarked: false,
      markingDialogVisible: false,
      selection: { x: 0, y: 0, width: 0, height: 0 },
      selectionStart: null,
      selectionPointerId: null,
      selectedDirection: 'up',
      viewpointNote: '',
      localLayoutNote: '',
      viewDirections: [
        { value: 'up', shortLabel: '向上看', label: '镜头从户型图下侧看向上侧', rotation: -90 },
        { value: 'right', shortLabel: '向右看', label: '镜头从户型图左侧看向右侧', rotation: 0 },
        { value: 'down', shortLabel: '向下看', label: '镜头从户型图上侧看向下侧', rotation: 90 },
        { value: 'left', shortLabel: '向左看', label: '镜头从户型图右侧看向左侧', rotation: 180 }
      ],
      referenceSpaces: ['全屋通用', ...SPACE_OPTIONS],
      referenceItems: [],
      generating: false,
      progress: 0,
      generationStage: '准备中',
      apiConfigured: false,
      records: [],
      recordsLoading: false,
      recordsPage: 1,
      recordsTotal: 0,
      deletingRecordIds: [],
      deletedRecordIds: [],
      pollingStopped: false,
      requestControllers: [],
      activePolls: {},
      pendingRecordTimer: null,
      refineDialogVisible: false,
      refining: false,
      refineMode: 'style',
      refinePrompt: '',
      refineDraft: {
        recordId: '',
        sourceTaskId: '',
        image: '',
        index: 0,
        space: '',
        style: '',
        sourcePrompt: '',
        constraintImageUrls: []
      },
      viewerScale: 1,
      viewerDragging: false,
      viewerPointerId: null,
      viewerDragStartX: 0,
      viewerDragStartY: 0,
      viewerDragScrollLeft: 0,
      viewerDragScrollTop: 0,
      imageViewer: { open: false, src: '', title: '' }
    }
  },
  computed: {
    applicableReferenceItems() {
      return this.referenceItemsForSpace(this.selectedSpace)
    },
    selectionValid() {
      return this.selection.width >= 0.03 && this.selection.height >= 0.03
    },
    selectionStyle() {
      return {
        left: `${this.selection.x * 100}%`,
        top: `${this.selection.y * 100}%`,
        width: `${this.selection.width * 100}%`,
        height: `${this.selection.height * 100}%`
      }
    },
    selectedDirectionItem() {
      return this.viewDirections.find(item => item.value === this.selectedDirection) || this.viewDirections[0]
    },
    selectedDirectionText() {
      return this.viewpointNote.trim() || this.selectedDirectionItem.label
    },
    directionArrowStyle() {
      const positions = {
        up: { left: '50%', top: '72%' },
        right: { left: '28%', top: '50%' },
        down: { left: '50%', top: '28%' },
        left: { left: '72%', top: '50%' }
      }
      return {
        ...(positions[this.selectedDirection] || positions.up),
        transform: `translate(-50%, -50%) rotate(${this.selectedDirectionItem.rotation}deg)`
      }
    },
    selectionGuidance() {
      return this.selectedSpace === '客餐厅'
        ? '请把客厅和餐厅一起框住；相邻书房、玄关等只带少量边缘'
        : '框住目标空间主体，可带少量相邻区域帮助识别门洞和连接关系'
    },
    refineDialogTitle() {
      return this.refineMode === 'style' ? '选定户型结构稿并套用风格' : '基于选中效果图生成精细版'
    },
    promptPresetItems() {
      if (!this.promptPresets) return []
      return [
        { key: 'writer', order: '01', ...this.promptPresets.writer },
        { key: 'enhancer', order: '02', ...this.promptPresets.enhancer }
      ].filter(item => item.systemPrompt)
    }
  },
  watch: {
    preset: {
      immediate: true,
      handler(preset) {
        if (preset) this.applyCreationPreset(preset)
      }
    }
  },
  created() {
    this.checkApiStatus()
    this.loadBackendRecords()
  },
  beforeDestroy() {
    this.pollingStopped = true
    if (this.pendingRecordTimer) window.clearTimeout(this.pendingRecordTimer)
    this.requestControllers.forEach(controller => controller.abort())
    this.requestControllers = []
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
    this.releaseReferencePreviews()
    document.body.classList.remove('viewer-open')
  },
  methods: {
    formatModelName,
    formatUserFacingBrand,
    async applyCreationPreset(preset) {
      const payload = preset && preset.data
      if (!payload) return

      if (preset.type === 'project') {
        this.selectedStyle = this.styles.includes(payload.style) ? payload.style : '现代简约'
        this.selectedSpace = '客餐厅'
        this.prompt = `${payload.requirement || ''} 项目面积约 ${payload.area || '--'}㎡，整体采用${payload.style || '现代简约'}风格。`.trim().slice(0, 500)
        this.showToast(`已带入“${payload.name}”的项目需求`)
        return
      }

      if (preset.type !== 'case') return
      this.selectedStyle = this.styles.includes(payload.style) ? payload.style : '现代简约'
      this.selectedSpace = this.spaces.includes(payload.space) ? payload.space : '客餐厅'
      this.prompt = String(payload.prompt || '').slice(0, 500)

      if (this.referenceItems.length >= 3) {
        this.showToast(`已带入“${payload.title}”的风格和描述，参考图已达到 3 张上限`)
        return
      }

      const referenceItem = {
        id: `case-reference-${payload.id}-${Date.now()}`,
        file: null,
        previewUrl: payload.image,
        sourceUrl: payload.image,
        space: this.selectedSpace,
        uploadedUrl: /^https?:\/\//i.test(payload.image) ? payload.image : ''
      }
      this.referenceItems = [...this.referenceItems, referenceItem]

      try {
        const response = await fetch(payload.image)
        if (!response.ok) throw new Error('案例图片读取失败')
        const blob = await response.blob()
        const extension = blob.type.includes('png') ? 'png' : 'jpg'
        referenceItem.file = new File([blob], `${payload.id}.${extension}`, {
          type: blob.type || 'image/jpeg'
        })
        referenceItem.previewUrl = URL.createObjectURL(referenceItem.file)
        referenceItem.uploadedUrl = ''
      } catch (error) {
        // 跨域图片无法转为文件时，仍保留原地址作为视觉参考。
      }

      this.showToast(`已把“${payload.title}”带入 AI 创作`)
    },
    async request(url, options = {}) {
      const {
        timeoutMs = 30000,
        retries,
        retryDelayMs = 600,
        timeoutMessage = '请求等待超时',
        ...fetchOptions
      } = options
      const method = String(fetchOptions.method || 'GET').toUpperCase()
      const retryCount = retries == null ? (method === 'GET' ? 1 : 0) : Math.max(0, retries)
      let lastError = null

      for (let attempt = 0; attempt <= retryCount; attempt += 1) {
        const controller = new AbortController()
        this.requestControllers.push(controller)
        const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs)
        try {
          const response = await fetch(url, { ...fetchOptions, signal: controller.signal })
          const responseText = await response.text()
          let result = {}
          if (responseText) {
            try {
              result = JSON.parse(responseText)
            } catch (error) {
              result = {}
            }
          }
          if (!response.ok) {
            const requestError = new Error(result.message || `请求失败（${response.status}）`)
            requestError.status = response.status
            requestError.code = result.code || 'REQUEST_FAILED'
            requestError.suggestion = result.suggestion || '请检查网络和输入内容后重新尝试。'
            requestError.retryable = typeof result.retryable === 'boolean'
              ? result.retryable
              : response.status === 429 || response.status >= 500
            throw requestError
          }
          return result
        } catch (error) {
          const requestError = error.name === 'AbortError' ? new Error(timeoutMessage) : error
          requestError.retryable = Boolean(requestError.retryable || error.name === 'AbortError' || error instanceof TypeError)
          lastError = requestError
          if (attempt >= retryCount || !requestError.retryable || this.pollingStopped) throw requestError
        } finally {
          window.clearTimeout(timeoutId)
          const controllerIndex = this.requestControllers.indexOf(controller)
          if (controllerIndex >= 0) this.requestControllers.splice(controllerIndex, 1)
        }
        await new Promise(resolve => window.setTimeout(resolve, retryDelayMs * (attempt + 1)))
      }
      throw lastError || new Error('请求失败，请稍后重试')
    },
    async requestPromptStream(url, body, onChunk) {
      const controller = new AbortController()
      this.requestControllers.push(controller)
      const timeoutId = window.setTimeout(() => controller.abort(), 90000)
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
          body: JSON.stringify(body),
          signal: controller.signal
        })
        if (!response.ok) {
          const responseText = await response.text()
          let message = `请求失败（${response.status}）`
          try {
            message = JSON.parse(responseText).message || message
          } catch (error) {
            // Keep the status-based message when the server did not return JSON.
          }
          throw new Error(message)
        }
        if (!response.body) throw new Error('当前浏览器不支持流式输出')

        const reader = response.body.getReader()
        const decoder = new TextDecoder('utf-8')
        let buffer = ''
        let finalPrompt = ''
        let streamError = ''

        const handleEvent = frame => {
          let eventName = 'message'
          const dataLines = []
          frame.split('\n').forEach(line => {
            if (line.startsWith('event:')) eventName = line.slice(6).trim()
            if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart())
          })
          if (!dataLines.length) return
          let payload = {}
          try {
            payload = JSON.parse(dataLines.join('\n'))
          } catch (error) {
            return
          }
          if (eventName === 'chunk' && payload.text) onChunk(String(payload.text))
          if (eventName === 'done') finalPrompt = String(payload.prompt || '')
          if (eventName === 'error') streamError = payload.message || 'AI 写作失败，请稍后重试'
        }

        while (true) {
          const { value, done } = await reader.read()
          buffer += decoder.decode(value || new Uint8Array(), { stream: !done }).replace(/\r\n/g, '\n')
          let boundary = buffer.indexOf('\n\n')
          while (boundary >= 0) {
            handleEvent(buffer.slice(0, boundary))
            buffer = buffer.slice(boundary + 2)
            boundary = buffer.indexOf('\n\n')
          }
          if (streamError) {
            await reader.cancel()
            throw new Error(streamError)
          }
          if (done) break
        }
        if (buffer.trim()) handleEvent(buffer)
        if (streamError) throw new Error(streamError)
        return { prompt: finalPrompt }
      } catch (error) {
        if (error.name === 'AbortError') throw new Error('AI 写作等待超时，请稍后重试')
        throw error
      } finally {
        window.clearTimeout(timeoutId)
        const controllerIndex = this.requestControllers.indexOf(controller)
        if (controllerIndex >= 0) this.requestControllers.splice(controllerIndex, 1)
      }
    },
    async checkApiStatus() {
      try {
        const result = await this.request('/api/health')
        this.apiConfigured = Boolean(result.configured)
      } catch (error) {
        this.apiConfigured = false
      }
    },
    showToast(message) {
      this.$message({ message, showClose: true, duration: 2200, offset: 24 })
    },
    togglePromptPresets() {
      this.promptPresetVisible = !this.promptPresetVisible
      if (this.promptPresetVisible && !this.promptPresets) this.loadPromptPresets()
    },
    async loadPromptPresets() {
      if (this.promptPresetsLoading) return
      this.promptPresetsLoading = true
      this.promptPresetsError = ''
      try {
        this.promptPresets = await this.request('/api/prompts/floorplan-presets')
      } catch (error) {
        this.promptPresetsError = error.message || '系统预设读取失败'
      } finally {
        this.promptPresetsLoading = false
      }
    },
    selectSpace(space) {
      if (this.selectedSpace === space) return
      this.selectedSpace = space
      if (this.floorPlanMarked) {
        this.floorPlanMarked = false
        this.localLayoutNote = ''
        this.showToast('空间已切换，请重新确认户型图上的标记区域')
      }
    },
    handleFile(event) {
      const file = event.target.files[0]
      if (!file) return
      if (!this.isSupportedImage(file)) {
        this.showToast('户型图仅支持 JPG、PNG 或 WebP 格式')
        event.target.value = ''
        return
      }
      if (file.size >= 10 * 1024 * 1024) {
        this.showToast('户型图必须小于 10MB，请压缩后再上传')
        event.target.value = ''
        return
      }
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
      const previewUrl = URL.createObjectURL(file)
      const image = new Image()
      image.onload = () => {
        if (previewUrl !== this.previewUrl) return
        this.selectedRatio = closestImageRatio(image.naturalWidth, image.naturalHeight, this.ratioOptions)
      }
      image.src = previewUrl
      this.floorPlanFile = file
      this.previewUrl = previewUrl
      this.floorPlanMarked = false
      this.selection = { x: 0, y: 0, width: 0, height: 0 }
      this.localLayoutNote = ''
      event.target.value = ''
      this.showToast('户型图上传成功，请框选本次生成区域')
      this.$nextTick(() => {
        this.markingDialogVisible = true
      })
    },
    openFloorPlanMarking() {
      if (!this.floorPlanFile) {
        this.showToast('请先上传户型图')
        return
      }
      this.markingDialogVisible = true
    },
    clearSelection() {
      this.selection = { x: 0, y: 0, width: 0, height: 0 }
      this.floorPlanMarked = false
    },
    pointInMarkingStage(event) {
      const stage = this.$refs.markingStage
      if (!stage) return null
      const rect = stage.getBoundingClientRect()
      return {
        x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
        y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height))
      }
    },
    startSelection(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return
      const point = this.pointInMarkingStage(event)
      if (!point) return
      this.selectionPointerId = event.pointerId
      this.selectionStart = point
      this.selection = { x: point.x, y: point.y, width: 0, height: 0 }
      this.floorPlanMarked = false
      if (event.currentTarget.setPointerCapture) event.currentTarget.setPointerCapture(event.pointerId)
      event.preventDefault()
    },
    moveSelection(event) {
      if (this.selectionPointerId !== event.pointerId || !this.selectionStart) return
      const point = this.pointInMarkingStage(event)
      if (!point) return
      const x = Math.min(this.selectionStart.x, point.x)
      const y = Math.min(this.selectionStart.y, point.y)
      this.selection = {
        x,
        y,
        width: Math.abs(point.x - this.selectionStart.x),
        height: Math.abs(point.y - this.selectionStart.y)
      }
      event.preventDefault()
    },
    endSelection(event) {
      if (this.selectionPointerId !== event.pointerId) return
      const stage = this.$refs.markingStage
      if (stage && stage.hasPointerCapture && stage.hasPointerCapture(event.pointerId)) {
        stage.releasePointerCapture(event.pointerId)
      }
      this.selectionPointerId = null
      this.selectionStart = null
      if (!this.selectionValid) {
        this.selection = { x: 0, y: 0, width: 0, height: 0 }
        this.showToast('框选区域太小，请重新拖动框选')
      }
    },
    confirmFloorPlanMarking() {
      if (!this.selectionValid || !this.selectedDirection) return
      this.floorPlanMarked = true
      this.markingDialogVisible = false
      this.showToast(`已锁定“${this.selectedSpace}”区域和镜头方向`)
    },
    handleReferenceFiles(event) {
      const files = Array.from(event.target.files || [])
      const remainingCount = Math.max(0, 3 - this.referenceItems.length)
      const validFiles = files
        .filter(file => this.isSupportedImage(file) && file.size < 10 * 1024 * 1024)
        .slice(0, remainingCount)
      const rejectedCount = files.length - validFiles.length
      const newItems = validFiles.map((file, index) => ({
        id: `floorplan-reference-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        previewUrl: URL.createObjectURL(file),
        space: '全屋通用',
        uploadedUrl: ''
      }))
      this.referenceItems = [...this.referenceItems, ...newItems]
      event.target.value = ''
      if (!newItems.length) {
        this.showToast(this.referenceItems.length >= 3
          ? 'AI创作最多上传 3 张参考图'
          : '参考图仅支持 JPG、PNG、WebP，且每张不能超过 10MB')
        return
      }
      this.showToast(rejectedCount ? `已添加 ${newItems.length} 张，其余图片已忽略` : `已添加 ${newItems.length} 张风格参考图`)
    },
    removeReferenceImage(item) {
      if (item.previewUrl && item.file) URL.revokeObjectURL(item.previewUrl)
      this.referenceItems = this.referenceItems.filter(reference => reference.id !== item.id)
      this.showToast('参考图已删除')
    },
    releaseReferencePreviews() {
      this.referenceItems.forEach(item => {
        if (item.previewUrl && item.file) URL.revokeObjectURL(item.previewUrl)
      })
    },
    isSupportedImage(file) {
      if (!file) return false
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp']
      if (allowedTypes.includes(String(file.type || '').toLowerCase())) return true
      return !file.type && /\.(jpe?g|png|webp)$/i.test(file.name || '')
    },
    referenceItemsForSpace(space) {
      const currentSpaceItems = this.referenceItems.filter(item => item.space === space)
      const universalItems = this.referenceItems.filter(item => item.space === '全屋通用')
      return [...currentSpaceItems, ...universalItems]
    },
    addQuickPrompt(text) {
      if (this.prompt.includes(text)) return
      this.prompt = this.prompt ? `${this.prompt}，${text}` : text
    },
    async uploadAsset(file) {
      const form = new FormData()
      form.append('file', file)
      const result = await this.request('/api/images/upload', { method: 'POST', body: form })
      if (!result.url) throw new Error('图片上传成功但未返回可用地址')
      return { url: result.url, localUrl: result.localUrl || '' }
    },
    async uploadImage(file) {
      const asset = await this.uploadAsset(file)
      return asset.url
    },
    async uploadReferenceImages(items = this.applicableReferenceItems) {
      return Promise.all(items.map(async item => {
        if (!item.uploadedAsset && item.file) item.uploadedAsset = await this.uploadAsset(item.file)
        if (!item.uploadedAsset && item.uploadedUrl) {
          item.uploadedAsset = { url: item.uploadedUrl, localUrl: item.localUrl || '' }
        }
        if (!item.uploadedAsset?.url) throw new Error('参考图缺少可用图片地址')
        return item.uploadedAsset
      }))
    },
    currentAnnotation() {
      return {
        x: this.selection.x,
        y: this.selection.y,
        width: this.selection.width,
        height: this.selection.height,
        direction: this.selectedDirection,
        viewpoint: this.selectedDirectionText,
        layoutNote: this.localLayoutNote.trim()
      }
    },
    floorPlanSelectionInstruction() {
      return `${selectionCoordinateText(this.selection, `${this.selectedSpace}目标区域`)}；${this.selectedDirectionText}`
    },
    ratioFrameStyle(ratio, maxWidth = 44, maxHeight = 30) {
      const [rawWidth, rawHeight] = String(ratio || '16:9').split(':').map(Number)
      const width = Number.isFinite(rawWidth) && rawWidth > 0 ? rawWidth : 16
      const height = Number.isFinite(rawHeight) && rawHeight > 0 ? rawHeight : 9
      const scale = Math.min(maxWidth / width, maxHeight / height)
      return {
        width: `${Math.max(4, Math.round(width * scale))}px`,
        height: `${Math.max(4, Math.round(height * scale))}px`
      }
    },
    ratioOutputText(ratio = this.selectedRatio) {
      const [width, height] = String(ratio || '16:9').split(':').map(Number)
      if (width === height) return `${ratio}方形构图`
      return width > height ? `${ratio}横向构图` : `${ratio}竖向构图`
    },
    validatePromptPrerequisites({ requirePrompt = false } = {}) {
      if (!this.floorPlanFile) throw new Error('请先上传平面图')
      if (!this.floorPlanMarked || !this.selectionValid) {
        throw new Error('请先在平面图上框选目标空间并确认镜头方向')
      }
      if (!this.selectedSpace) throw new Error('请选择空间')
      if (!this.selectedStyle) throw new Error('请选择装修风格')
      if (!this.referenceItemsForSpace(this.selectedSpace).length) {
        throw new Error(`请至少添加一张适用于“${this.selectedSpace}”或“全屋通用”的参考图`)
      }
      if (requirePrompt && !this.prompt.trim()) throw new Error('请填写效果图风格要求，或先使用“AI 帮我写”')
    },
    async preparePromptVisualContext() {
      this.validatePromptPrerequisites()
      const applicableItems = this.referenceItemsForSpace(this.selectedSpace)
      const [contextFile, targetFile] = await this.createConstraintFiles()
      const [floorPlanAssets, referenceAssets] = await Promise.all([
        Promise.all([
          this.uploadAsset(this.floorPlanFile),
          this.uploadAsset(contextFile),
          this.uploadAsset(targetFile)
        ]),
        this.uploadReferenceImages(applicableItems)
      ])
      return {
        floorPlanImageUrls: floorPlanAssets.map(asset => asset.url),
        floorPlanRecordUrls: floorPlanAssets.map(asset => asset.localUrl).filter(Boolean),
        referenceImageUrls: referenceAssets.map(asset => asset.url),
        referenceRecordUrls: referenceAssets.map(asset => asset.localUrl).filter(Boolean),
        annotation: this.currentAnnotation(),
        layoutNote: this.localLayoutNote.trim(),
        viewpoint: this.floorPlanSelectionInstruction()
      }
    },
    async writePrompt() {
      if (this.writingPrompt || this.enhancingPrompt) return
      if (!this.apiConfigured) {
        this.showToast('请先在 .env 文件中配置 ToAPIs API Key')
        return
      }
      this.writingPrompt = true
      const previousPrompt = this.prompt
      let streamedPrompt = ''
      try {
        const context = await this.preparePromptVisualContext()
        this.prompt = ''
        const result = await this.requestPromptStream('/api/prompts/write-floorplan-style-stream', {
          imageUrls: context.referenceImageUrls,
          floorPlanImageUrls: context.floorPlanImageUrls,
          layoutNote: context.layoutNote,
          viewpoint: context.viewpoint,
          space: this.selectedSpace,
          style: this.selectedStyle
        }, chunk => {
          streamedPrompt += chunk
          this.prompt = streamedPrompt.slice(0, 500)
        })
        const writtenPrompt = String(result.prompt || streamedPrompt).trim()
        if (!writtenPrompt) throw new Error('AI 未返回可用的提示词')
        this.prompt = writtenPrompt.slice(0, 500)
        this.showToast('AI 已综合平面图、参考图、空间和装修风格完成撰写')
      } catch (error) {
        if (!streamedPrompt) this.prompt = previousPrompt
        this.showToast(error.message || 'AI 写作失败，请稍后重试')
      } finally {
        this.writingPrompt = false
      }
    },
    async enhancePrompt() {
      if (this.writingPrompt || this.enhancingPrompt) return
      if (!this.prompt.trim()) {
        this.showToast('请先输入需要完善的内容')
        return
      }
      if (!this.apiConfigured) {
        this.showToast('请先在 .env 文件中配置 ToAPIs API Key')
        return
      }
      this.enhancingPrompt = true
      try {
        const context = await this.preparePromptVisualContext()
        const result = await this.request('/api/prompts/enhance-floorplan-style', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            prompt: this.prompt.trim(),
            imageUrls: context.referenceImageUrls,
            floorPlanImageUrls: context.floorPlanImageUrls,
            layoutNote: context.layoutNote,
            viewpoint: context.viewpoint,
            space: this.selectedSpace,
            style: this.selectedStyle
          }),
          timeoutMs: 50000
        })
        const enhancedPrompt = String(result.prompt || '').trim()
        if (!enhancedPrompt) throw new Error('AI 未返回可用的空间描述')
        this.prompt = enhancedPrompt.slice(0, 500)
        this.showToast('AI 已结合全部图片和设置完善提示词')
      } catch (error) {
        this.showToast(error.message || 'AI 完善失败，请稍后重试')
      } finally {
        this.enhancingPrompt = false
      }
    },
    async resolveGenerationStylePrompt(visualContext) {
      const applicableItems = this.referenceItemsForSpace(this.selectedSpace)
      const styleParts = []
      if (applicableItems.length) {
        this.generationStage = '提取参考图风格'
        const referenceImageUrls = visualContext.referenceImageUrls
        try {
          const result = await this.request('/api/prompts/write-floorplan-style', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              imageUrls: referenceImageUrls,
              floorPlanImageUrls: visualContext.floorPlanImageUrls,
              layoutNote: visualContext.layoutNote,
              viewpoint: visualContext.viewpoint,
              space: this.selectedSpace,
              style: this.selectedStyle
            }),
            timeoutMs: 50000
          })
          const referenceStyle = String(result.prompt || '').trim()
          if (!referenceStyle) throw new Error('未能从参考图提取风格')
          styleParts.push(referenceStyle)
        } catch (error) {
          styleParts.push(`${this.selectedStyle}，材质真实，灯光自然，家具与软装完整`)
          this.showToast('参考图风格识别暂时不可用，已按所选风格继续生成')
        }
      }
      if (this.prompt.trim()) styleParts.push(this.prompt.trim())
      const combinedStyle = styleParts.join('；') || `${this.selectedStyle}，材质真实，灯光自然，家具与软装完整`
      if (!this.prompt.trim()) return combinedStyle

      this.generationStage = '整理纯风格要求'
      const result = await this.request('/api/prompts/enhance-floorplan-style', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: combinedStyle,
          imageUrls: visualContext.referenceImageUrls,
          floorPlanImageUrls: visualContext.floorPlanImageUrls,
          layoutNote: visualContext.layoutNote,
          viewpoint: visualContext.viewpoint,
          space: this.selectedSpace,
          style: this.selectedStyle
        })
      })
      return String(result.prompt || '').trim() || combinedStyle
    },
    resetForm() {
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
      this.releaseReferencePreviews()
      this.previewUrl = ''
      this.floorPlanFile = null
      this.floorPlanMarked = false
      this.selection = { x: 0, y: 0, width: 0, height: 0 }
      this.selectedDirection = 'up'
      this.viewpointNote = ''
      this.localLayoutNote = ''
      this.referenceItems = []
      this.selectedSpace = '客餐厅'
      this.selectedStyle = '现代简约'
      this.selectedRatio = '16:9'
      this.selectedResolution = MINIMUM_OUTPUT_RESOLUTION
      this.prompt = ''
      this.showToast('AI创作设置已清空')
    },
    loadImage(url) {
      return new Promise((resolve, reject) => {
        const image = new Image()
        image.onload = () => resolve(image)
        image.onerror = () => reject(new Error('户型图读取失败'))
        image.src = url
      })
    },
    canvasToFile(canvas, filename) {
      return new Promise((resolve, reject) => {
        canvas.toBlob(blob => {
          if (!blob) {
            reject(new Error('户型约束图生成失败'))
            return
          }
          resolve(new File([blob], filename, { type: 'image/png' }))
        }, 'image/png', 0.92)
      })
    },
    async createConstraintFiles() {
      const image = await this.loadImage(this.previewUrl)
      const scale = Math.min(1, 2200 / Math.max(image.naturalWidth, image.naturalHeight))
      const width = Math.max(1, Math.round(image.naturalWidth * scale))
      const height = Math.max(1, Math.round(image.naturalHeight * scale))
      const selectionX = Math.round(this.selection.x * width)
      const selectionY = Math.round(this.selection.y * height)
      const selectionWidth = Math.max(1, Math.round(this.selection.width * width))
      const selectionHeight = Math.max(1, Math.round(this.selection.height * height))

      const createCleanCrop = (paddingFactor, minimumPaddingFactor) => {
        const paddingX = Math.round(Math.max(selectionWidth * paddingFactor, width * minimumPaddingFactor))
        const paddingY = Math.round(Math.max(selectionHeight * paddingFactor, height * minimumPaddingFactor))
        const cropX = Math.max(0, selectionX - paddingX)
        const cropY = Math.max(0, selectionY - paddingY)
        const cropRight = Math.min(width, selectionX + selectionWidth + paddingX)
        const cropBottom = Math.min(height, selectionY + selectionHeight + paddingY)
        const cropWidth = Math.max(1, cropRight - cropX)
        const cropHeight = Math.max(1, cropBottom - cropY)
        const canvas = document.createElement('canvas')
        canvas.width = cropWidth
        canvas.height = cropHeight
        canvas.getContext('2d').drawImage(
          image,
          cropX / scale,
          cropY / scale,
          cropWidth / scale,
          cropHeight / scale,
          0,
          0,
          cropWidth,
          cropHeight
        )
        return canvas
      }

      // Both support images contain only source pixels. The wider crop provides
      // room relationships; the tighter crop provides detail without UI marks.
      const contextCanvas = createCleanCrop(0.75, 0.08)
      const targetCanvas = createCleanCrop(0.12, 0.015)

      return Promise.all([
        this.canvasToFile(contextCanvas, `目标区域上下文-无标记-${Date.now()}.png`),
        this.canvasToFile(targetCanvas, `目标区域近景-无标记-${Date.now()}.png`)
      ])
    },
    buildFloorplanRenderPrompt(styleDescription, ratio = this.selectedRatio) {
      const layoutNote = this.localLayoutNote.trim() || '未补充人工说明，请只根据三张平面图识别，不确定的结构不得自行补充'
      const styleRequirement = String(styleDescription || '').trim() || `${this.selectedStyle}，材质真实，灯光自然`
      return `${FLOORPLAN_MODE_MARKER}
【户型约束效果图：平面图是唯一结构来源】
第1张图片是客户干净原始户型图，是最高优先级的强制结构约束。
第2张图片是目标区域周边的无标记上下文图，用于核对相邻空间和连接关系。
第3张图片是目标区域的无标记近景图，用于核对墙体、门窗、开口和固定家具细节。
${this.floorPlanSelectionInstruction()}。页面上的蓝框、标签和方向箭头仅用于交互，未写入以上任何图片；目标区域和镜头只以本行坐标文字为准。
本次输入只有以上3张户型相关图片，没有传入任何风格参考图。风格参考图已经提前转换为下方的纯风格文字，绝不能据此想象或改变户型。

生成任务：依据第1至第3张平面图，为框选的${this.selectedSpace}生成一张完整、写实、可用于设计沟通的室内装修效果图。
镜头要求：${this.selectedDirectionText}。
局部关系说明：${layoutNote}。
风格要求：${styleRequirement}。
结构优先规则：风格文字只能控制配色、材质、家具造型语言、灯具、软装和光线氛围；如与平面图中的墙体、门窗、开口、阳台、通道或空间连接冲突，必须忽略风格文字，以平面图为准。
全图约束：画面中凡是超出指定坐标区域但仍然可见的部分，必须回到第1张完整户型图核对其房间名称、墙体、门窗、开口和连接方向，不得根据常见案例自行补齐。
强制保持：平面图上下左右方向不得旋转或镜像；墙体轮廓、门窗位置、开口方向、柱体、阳台关系、相邻空间顺序和主要尺度比例不得改变。
禁止事项：不得新增或移动落地窗、普通窗、门洞、墙体、通道或房间，不得输出白模、结构核对稿、平面图、鸟瞰图或拼图。
输出要求：单张写实室内装修效果图，采用${this.ratioOutputText(ratio)}，具备完整材质、家具、灯光和软装，不要文字、水印、户型标注、框线、遮罩或箭头。`
    },
    buildStylePrompt(
      description = this.prompt,
      { space = this.selectedSpace, style = this.selectedStyle, layoutNote = this.localLayoutNote, ratio = this.selectedRatio } = {}
    ) {
      const styleRequirement = description.trim() || `${style}，材质真实，灯光自然`
      const localRelation = String(layoutNote || '').trim() || '未补充人工说明，所有结构以完整平面图和已选结构稿为准'
      return `${FLOORPLAN_MODE_MARKER}
【第二阶段：锁定户型，只套用风格】
第1张图片是用户已确认的户型结构透视稿，是镜头、墙体、门窗、开口、相邻空间和家具位置的最高优先级依据。
第2张图片是客户原始完整户型图，负责校验画面内以及画面外延伸区域的全屋关系。
第3张图片是目标区域周边的无标记上下文图。
第4张图片是目标区域的无标记近景图。
${this.floorPlanSelectionInstruction()}。页面框线、标签和方向箭头不在任何输入图片中，目标区域和镜头只以本行坐标文字为准。
第5张及之后的图片才是风格参考图，只允许提取配色、材质、家具造型语言、灯具造型、软装质感和光线氛围。

生成任务：在第1张结构稿上为${space}套用${style}风格，输出写实室内效果图。
镜头要求：完全锁定第1张的摄影机位置、朝向、焦段和构图，不得重新取景。
局部关系说明：${localRelation}。
风格要求：${styleRequirement}。
结构优先规则：任何风格文字或参考图只要与第1至第4张的墙体、门窗、开口、阳台、通道、相邻空间或家具位置冲突，必须忽略风格图和风格文字，以户型结构为准。
全图约束：画面中超出目标区域的可见部分必须依据第2张完整户型图还原，禁止自行增加落地窗、门洞、墙体、走廊或房间。
禁止事项：不得照搬风格参考图的户型、窗户、构图和家具摆放，不得旋转或镜像空间，不得改变已确认结构稿的主要尺度关系。
输出要求：单张写实室内效果图，${this.ratioOutputText(ratio)}，不要文字、水印、户型标注、框线、遮罩、箭头或拼图。`
    },
    extractUserPrompt(prompt) {
      const content = String(prompt || '')
      const styleMatch = content.match(/风格要求：([\s\S]*?)。\s*结构优先规则：/)
      if (styleMatch) return styleMatch[1].trim()
      return content.includes('【第一阶段：只生成户型结构稿') ? '仅核对户型结构，选定后再套用风格' : '保持户型结构'
    },
    statusText(status) {
      return { generating: '生成中', completed: '已完成', failed: '生成失败' }[status] || status
    },
    formatRecordTime(value) {
      if (!value) return '时间未知'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '时间未知'
      return new Intl.DateTimeFormat('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).format(date)
    },
    stageText(stage) {
      return {
        structure: '户型结构稿',
        floorplan: '户型效果图',
        style: '风格效果图',
        detail: '精细版',
        white_model: '黑底白线稿',
        sketch_plan: '线稿出方案',
        furniture_cutout: '家具白底图',
        concept: '方向稿'
      }[stage] || '方向稿'
    },
    createImageSlots(images = [], minimumCount = 0) {
      const highestSortOrder = images.reduce((highest, image, index) => Math.max(highest, Number(image?.sortOrder || index + 1)), 0)
      const slots = Array(Math.max(highestSortOrder, minimumCount)).fill('')
      images.forEach((image, index) => {
        const slotIndex = Number(image?.sortOrder || index + 1) - 1
        if (slotIndex >= 0 && slotIndex < slots.length) slots[slotIndex] = image?.url || image || ''
      })
      return slots
    },
    normalizeInputContext(context = {}) {
      return {
        floorPlanImages: Array.isArray(context.floorPlanImages) ? context.floorPlanImages : [],
        referenceImages: Array.isArray(context.referenceImages) ? context.referenceImages : [],
        annotation: context.annotation || null
      }
    },
    inputContextFromVisualContext(context = {}) {
      const floorPlanLabels = [
        { type: 'original', label: '原始平面图（无标记）' },
        { type: 'annotated', label: '目标区域上下文（无标记）' },
        { type: 'crop', label: '目标区域近景（无标记）' }
      ]
      return {
        floorPlanImages: (context.floorPlanRecordUrls || []).map((url, index) => ({
          ...(floorPlanLabels[index] || { type: 'floorplan', label: `平面图 ${index + 1}` }),
          url
        })),
        referenceImages: (context.referenceRecordUrls || []).map((url, index) => ({
          type: 'reference',
          label: `参考图 ${index + 1}`,
          url
        })),
        annotation: context.annotation || null
      }
    },
    hasRecordInputContext(record) {
      return Boolean(
        this.uploadedFloorPlanImages(record).length || record.inputContext?.referenceImages?.length
      )
    },
    uploadedFloorPlanImages(record) {
      const images = record.inputContext?.floorPlanImages || []
      const originals = images.filter(image => image.type === 'original')
      return originals.length ? originals : images.slice(0, 1)
    },
    imageAt(record, index) {
      return record.images[index] || ''
    },
    taskStatus(record, index) {
      return record.taskStates[index]?.status || 'pending'
    },
    normalizeErrorDetails(error, fallbackMessage = 'AI 未能完成本次图片生成') {
      const source = error && typeof error === 'object' ? error : {}
      const message = typeof error === 'string' ? error : source.message
      return {
        code: source.code || 'GENERATION_FAILED',
        message: message || fallbackMessage,
        suggestion: source.suggestion || '请检查图片和描述后重新尝试；若仍失败，请联系管理员并提供任务编号。',
        retryable: source.retryable !== false
      }
    },
    taskErrorDetails(record, index) {
      const error = record.taskStates[index]?.error
      return error ? this.normalizeErrorDetails(error) : null
    },
    taskErrorMessage(record, index) {
      return this.taskErrorDetails(record, index)?.message || 'AI 未返回可用图片，请重新尝试'
    },
    recordErrorDetails(record) {
      if (record.errorDetails) return record.errorDetails
      const failedState = record.taskStates.find(state => state.status === 'failed' && state.error)
      if (failedState) return this.normalizeErrorDetails(failedState.error)
      if (record.error) return this.normalizeErrorDetails(record.error)
      return null
    },
    completedImageCount(record) {
      return record.images.filter(Boolean).length
    },
    recordSlotCount(record) {
      return Math.max(Number(record.expectedCount || 0), record.taskIds.length, this.completedImageCount(record), 1)
    },
    taskProgress(record, index) {
      const state = record.taskStates[index]
      if (!state) return '等待任务创建'
      if (state.status === 'completed') return '已完成 100%'
      if (state.status === 'failed') return this.taskErrorMessage(record, index)
      return `${state.status === 'pending' || state.status === 'queued' ? '排队中' : '生成中'} ${Number(state.progress || 0)}%`
    },
    async loadBackendRecords() {
      this.recordsLoading = true
      try {
        const response = await this.request(`/api/records/paged?stages=floorplan,structure,style,detail&page=${this.recordsPage}&pageSize=12`)
        this.recordsTotal = Number(response.total || 0)
        const records = response.items || []
        this.records = (this.recordsPage === 1 ? mergePendingGenerations(records, 'floorplan') : records)
          .slice(0, 12).map(item => ({
            id: item.id,
            space: item.space,
            style: item.style,
            size: item.size || '16:9',
            createdAt: item.createdAt,
            prompt: this.extractUserPrompt(item.prompt),
            technicalPrompt: item.prompt,
            constraintImageUrls: [],
            inputContext: this.normalizeInputContext(item.inputContext),
            images: this.createImageSlots(item.images || [], (item.tasks || []).length),
            taskIds: (item.tasks || []).map(task => task.id),
            taskStates: (item.tasks || []).map(task => ({
              status: task.status,
              progress: task.progress,
              imageModel: task.imageModel || '',
              error: task.error || null
            })),
            expectedCount: Math.max(Number(item.expectedCount || 0), (item.tasks || []).length, 1),
            status: item.status,
            stage: item.stage || 'concept',
            parentBatchId: item.parentBatchId || '',
            sourceTaskId: item.sourceTaskId || '',
            progress: item.progress,
            imageModel: item.imageModel || '',
            error: item.error || '',
            errorDetails: item.error ? this.normalizeErrorDetails(item.error) : null
          }))
        this.$nextTick(() => {
          this.resumePendingTasks()
          this.schedulePendingRecordRefresh()
        })
      } catch (error) {
        this.showToast('暂时无法读取 AI 创作历史记录')
      } finally {
        this.recordsLoading = false
      }
    },
    changeRecordsPage(page) {
      this.recordsPage = page
      this.loadBackendRecords()
    },
    async removeRecord(record) {
      if (this.deletingRecordIds.includes(record.id)) return
      try {
        await this.$confirm(
          `删除“${record.space} · ${record.style}”后将无法在创作记录中恢复，是否继续？`,
          '删除创作记录',
          {
            confirmButtonText: '删除',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
      } catch (error) {
        return
      }

      this.deletingRecordIds.push(record.id)
      try {
        const response = await fetch(`/api/records/${encodeURIComponent(record.id)}`, { method: 'DELETE' })
        if (!response.ok) throw new Error('删除失败')
      } catch (error) {
        this.showToast(error.message)
        return
      } finally {
        this.deletingRecordIds = this.deletingRecordIds.filter(id => id !== record.id)
      }
      this.deletedRecordIds.push(record.id)
      this.records = this.records.filter(item => item.id !== record.id)
      this.showToast('记录已删除')
    },
    finishRecord(record) {
      record.status = 'completed'
      record.progress = 100
      record.time = '刚刚完成'
    },
    failRecord(record, error) {
      record.status = 'failed'
      record.errorDetails = this.normalizeErrorDetails(error)
      record.error = record.errorDetails.message
      this.showToast(record.error)
    },
    resumePendingTasks() {
      this.records
        .filter(record => record.taskIds.length && record.taskStates.some(state => !['completed', 'failed'].includes(state.status)))
        .forEach(record => {
          record.status = 'generating'
          this.resumeRecord(record)
        })
    },
    async resumeRecord(record) {
      if (this.activePolls[record.id]) return
      this.$set(this.activePolls, record.id, true)
      try {
        await this.pollTasks(record.taskIds, record)
        if (this.deletedRecordIds.includes(record.id)) return
        this.finishRecord(record)
        this.showToast(`${record.space}的 ${record.taskIds.length} 张${this.stageText(record.stage)}已生成`)
      } catch (error) {
        if (error.deleted) return
        if (!this.pollingStopped && error.pending) {
          record.status = 'generating'
          record.time = '任务仍在后台处理，请稍后刷新查看'
          this.showToast(error.message)
        } else if (!this.pollingStopped) this.failRecord(record, error)
      } finally {
        this.$delete(this.activePolls, record.id)
      }
    },
    async pollTasks(taskIds, record) {
      const startedAt = Date.now()
      let consecutiveErrors = 0
      while (Date.now() - startedAt < 600000 && !this.pollingStopped && !this.deletedRecordIds.includes(record.id)) {
        let states = null
        try {
          states = await Promise.all(taskIds.map(id => this.request(`/api/generations/${encodeURIComponent(id)}`, {
            timeoutMs: 12000,
            retries: 1,
            timeoutMessage: '任务状态查询超时'
          })))
          consecutiveErrors = 0
        } catch (error) {
          consecutiveErrors += 1
          record.time = `网络波动，正在重新连接（${consecutiveErrors}/5）`
          if (consecutiveErrors >= 5) throw new Error('连续多次无法查询生成进度，请刷新页面后查看')
          await new Promise(resolve => window.setTimeout(resolve, 3000))
          continue
        }
        const average = states.reduce((sum, item) => sum + (item.status === 'completed' ? 100 : Number(item.progress || 0)), 0) / states.length
        record.taskStates = states.map(item => ({
          status: item.status,
          progress: Number(item.progress || 0),
          error: item.error || null
        }))
        states.forEach((item, index) => {
          const imageUrl = item.status === 'completed' ? item.result?.data?.[0]?.url : ''
          if (imageUrl && record.images[index] !== imageUrl) this.$set(record.images, index, imageUrl)
        })
        const completedCount = this.completedImageCount(record)
        record.progress = Math.min(99, Math.round(average))
        record.time = completedCount ? `已完成 ${completedCount} 张，其他方案继续生成` : '正在后台处理'
        this.generationStage = completedCount ? `已展示 ${completedCount}/${taskIds.length} 张` : 'AI 生成中'
        this.progress = Math.max(18, Math.min(99, Math.round(18 + average * 0.81)))
        const allFinished = states.every(item => ['completed', 'failed'].includes(item.status))
        if (allFinished) {
          const failed = states.find(item => item.status === 'failed')
          if (failed) {
            const failedError = new Error(failed.error?.message || '部分效果图生成失败')
            Object.assign(failedError, failed.error || {})
            throw failedError
          }
          return
        }
        await new Promise(resolve => setTimeout(resolve, 3000))
      }
      if (this.deletedRecordIds.includes(record.id)) {
        const deletedError = new Error('记录已删除')
        deletedError.deleted = true
        throw deletedError
      }
      if (this.pollingStopped) throw new Error('页面已关闭，任务将在下次打开时继续查询')
      const pendingError = new Error('生成时间较长，任务仍在后台继续，请稍后刷新查看')
      pendingError.pending = true
      throw pendingError
    },
    async startGenerate() {
      if (!this.apiConfigured) {
        this.showToast('请先在 .env 文件中配置 ToAPIs API Key')
        return
      }
      try {
        this.validatePromptPrerequisites({ requirePrompt: true })
      } catch (error) {
        this.showToast(error.message)
        if (this.floorPlanFile && (!this.floorPlanMarked || !this.selectionValid)) this.openFloorPlanMarking()
        return
      }

      this.generating = true
      this.progress = 3
      let currentRecord = null
      const requestId = this.createRequestId('generate')
      const generationSpace = this.selectedSpace
      const generationStyle = this.selectedStyle
      const generationRatio = this.selectedRatio
      const pendingId = `interior-${requestId}`
      const record = {
        id: pendingId,
        space: generationSpace,
        style: generationStyle,
        size: generationRatio,
        time: '任务已提交，正在准备生成资料',
        prompt: this.prompt,
        technicalPrompt: `${FLOORPLAN_MODE_MARKER}\n${this.prompt}`,
        constraintImageUrls: [],
        inputContext: this.normalizeInputContext(null),
        images: Array(1).fill(''),
        taskIds: [],
        taskStates: [],
        expectedCount: 1,
        status: 'generating',
        stage: 'floorplan',
        parentBatchId: '',
        sourceTaskId: '',
        progress: 0,
        error: ''
      }
      savePendingGeneration({
        id: record.id,
        space: record.space,
        style: record.style,
        size: record.size,
        prompt: record.technicalPrompt,
        status: record.status,
        stage: record.stage,
        progress: 0,
        expectedCount: 1,
        tasks: [],
        images: [],
        inputContext: record.inputContext,
        createdAt: new Date().toISOString()
      })
      currentRecord = record
      this.records = [record, ...this.records.filter(item => item.id !== record.id)]
      let generationRequestStarted = false
      try {
        this.generationStage = '制作户型约束'
        const visualContext = await this.preparePromptVisualContext()
        this.progress = 8

        const styleDescription = await this.resolveGenerationStylePrompt(visualContext)
        this.prompt = styleDescription.slice(0, 500)
        const imageUrls = visualContext.floorPlanImageUrls
        const technicalPrompt = this.buildFloorplanRenderPrompt(styleDescription, generationRatio)
        this.progress = 15

        this.generationStage = '创建效果图任务'
        record.time = '任务已提交，正在确认任务编号'
        record.prompt = styleDescription
        record.technicalPrompt = technicalPrompt
        record.constraintImageUrls = imageUrls
        record.inputContext = this.inputContextFromVisualContext(visualContext)
        savePendingGeneration({
          id: record.id,
          space: record.space,
          style: record.style,
          size: record.size,
          prompt: technicalPrompt,
          status: record.status,
          stage: record.stage,
          progress: 0,
          expectedCount: 1,
          tasks: [],
          images: [],
          inputContext: record.inputContext,
          createdAt: new Date().toISOString()
        })
        generationRequestStarted = true
        const batch = await this.request('/api/generations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageUrls,
            prompt: technicalPrompt,
            space: generationSpace,
            style: generationStyle,
            size: generationRatio,
            resolution: this.selectedResolution,
            quality: this.selectedQuality,
            count: 1,
            stage: 'floorplan',
            floorPlanRecordUrls: visualContext.floorPlanRecordUrls,
            referenceRecordUrls: visualContext.referenceRecordUrls,
            annotation: visualContext.annotation,
            requestId
          }),
          timeoutMs: 120000,
          retries: 1,
          timeoutMessage: '创建任务等待超时，正在确认是否已成功提交'
        })
        const returnedTasks = (batch.tasks || []).filter(item => item && item.id)
        const taskIds = returnedTasks.map(item => item.id)
        if (taskIds.length !== 1) {
          const pendingError = new Error('任务已保存，正在后台确认任务编号')
          pendingError.pending = true
          pendingError.retryable = true
          throw pendingError
        }

        record.id = batch.batchId || pendingId
        record.time = '刚刚创建'
        record.taskIds = taskIds
        record.taskStates = returnedTasks.map(item => ({
            status: item.status || 'pending',
            progress: Number(item.progress || 0),
            error: item.error || null
          }))
        removePendingGeneration(pendingId)
        this.generationStage = 'AI 绘制效果图'
        await this.pollTasks(taskIds, record)
        if (this.deletedRecordIds.includes(record.id)) return
        if (this.completedImageCount(record) !== 1) throw new Error('户型效果图未返回，请重试')
        this.progress = 100
        this.finishRecord(record)
        this.showToast('1 张户型效果图已生成')
      } catch (error) {
        if (error.deleted) return
        if (currentRecord && generationRequestStarted && (error.pending || error.retryable)) {
          currentRecord.status = 'generating'
          currentRecord.time = '任务已保存，正在后台确认，请稍后刷新查看'
          this.schedulePendingRecordRefresh()
          this.showToast(error.message)
        } else if (currentRecord) {
          if (!currentRecord.taskIds.length) removePendingGeneration(currentRecord.id)
          this.failRecord(currentRecord, error)
        }
        else this.showToast(error.message || '生成失败，请稍后重试')
      } finally {
        this.generating = false
      }
    },
    schedulePendingRecordRefresh() {
      if (this.pendingRecordTimer) window.clearTimeout(this.pendingRecordTimer)
      if (this.pollingStopped || !this.records.some(record => record.status === 'generating' && !record.taskIds.length)) return
      this.pendingRecordTimer = window.setTimeout(() => {
        this.pendingRecordTimer = null
        this.loadBackendRecords()
      }, 2000)
    },
    createRequestId(prefix) {
      const randomPart = window.crypto && typeof window.crypto.randomUUID === 'function'
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`
      return `${prefix}-${randomPart}`.slice(0, 64)
    },
    async retryRecord(record) {
      if (this.generating) return
      const imageUrls = (record.constraintImageUrls || []).filter(Boolean)
      if (record.stage !== 'floorplan' || imageUrls.length !== 3) {
        this.showToast('这条记录的原始约束图已不在当前会话中，请在上方重新上传户型图后生成')
        return
      }
      this.generating = true
      this.progress = 12
      this.generationStage = '重新创建任务'
      let retryRecord = null
      try {
        const batch = await this.request('/api/generations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageUrls,
            prompt: record.technicalPrompt,
            space: record.space,
            style: record.style,
            size: record.size,
            resolution: this.selectedResolution,
            quality: this.selectedQuality,
            count: this.recordSlotCount(record),
            stage: 'floorplan',
            floorPlanRecordUrls: (record.inputContext?.floorPlanImages || []).map(image => image.url).filter(Boolean),
            referenceRecordUrls: (record.inputContext?.referenceImages || []).map(image => image.url).filter(Boolean),
            annotation: record.inputContext?.annotation || null,
            requestId: this.createRequestId('retry')
          }),
          timeoutMs: 120000,
          retries: 1,
          timeoutMessage: '重新创建任务等待超时，正在确认是否已成功提交'
        })
        const returnedTasks = (batch.tasks || []).filter(item => item && item.id)
        if (!returnedTasks.length) throw new Error('AI 服务没有返回可查询的任务，请稍后重试')
        retryRecord = {
          ...record,
          id: batch.batchId || `floorplan-retry-${Date.now()}`,
          time: '刚刚重新创建',
          images: Array(returnedTasks.length).fill(''),
          taskIds: returnedTasks.map(item => item.id),
          taskStates: returnedTasks.map(item => ({
            status: item.status || 'pending',
            progress: Number(item.progress || 0),
            error: item.error || null
          })),
          status: 'generating',
          progress: 0,
          error: '',
          errorDetails: null
        }
        this.records.unshift(retryRecord)
        await this.pollTasks(retryRecord.taskIds, retryRecord)
        this.finishRecord(retryRecord)
        this.progress = 100
        this.showToast('重新生成已完成')
      } catch (error) {
        if (retryRecord && error.pending) {
          retryRecord.status = 'generating'
          retryRecord.time = '任务仍在后台处理，请稍后刷新查看'
        } else if (retryRecord) {
          this.failRecord(retryRecord, error)
        } else {
          const details = this.normalizeErrorDetails(error)
          this.showToast(`${details.message}；${details.suggestion}`)
        }
      } finally {
        this.generating = false
      }
    },
    openRefineDialog(record, index) {
      const sourceTaskId = record.taskIds[index]
      if (!sourceTaskId) {
        this.showToast('这张图片没有可用的生成任务，不能继续')
        return
      }
      const isStyleStep = record.stage === 'structure'
      if (isStyleStep && !this.applicableReferenceItems.length) {
        this.showToast(`请先在上方添加适用于“${record.space}”或“全屋通用”的风格参考图`)
        return
      }
      const constraintImageUrls = record.constraintImageUrls || []
      if (isStyleStep && !constraintImageUrls.length && (!this.floorPlanFile || !this.floorPlanMarked)) {
        this.showToast('历史结构稿需要重新上传同一张户型图并完成相同框选后，才能套用风格')
        return
      }
      this.refineMode = isStyleStep ? 'style' : 'detail'
      this.refineDraft = {
        recordId: record.id,
        sourceTaskId,
        image: this.imageAt(record, index),
        index,
        space: record.space,
        style: isStyleStep ? this.selectedStyle : record.style,
        size: record.size || this.selectedRatio,
        sourcePrompt: record.technicalPrompt || `${FLOORPLAN_MODE_MARKER} 保持当前户型约束`,
        constraintImageUrls,
        inputContext: this.normalizeInputContext(record.inputContext)
      }
      this.refinePrompt = isStyleStep
        ? (this.prompt.trim() || `${this.selectedStyle}，材质真实，灯光自然`)
        : '保持当前镜头、墙体、门窗和主要家具布局，加强材质纹理、灯光层次与软装细节'
      this.refineDialogVisible = true
    },
    async startRefinement() {
      if (this.refining) return
      if (!this.refineDraft.sourceTaskId) return
      if (!this.apiConfigured) {
        this.showToast('请先在 .env 文件中配置 ToAPIs API Key')
        return
      }
      this.refining = true
      let currentRecord = null
      const requestId = this.createRequestId('refine')
      const isStyleStep = this.refineMode === 'style'
      const detailRequirement = this.refinePrompt.trim() || (isStyleStep
        ? `${this.refineDraft.style}，材质真实，灯光自然`
        : '保持当前设计方向，提升材质和灯光真实度')
      try {
        let supportingImageUrls = []
        let floorPlanRecordUrls = []
        let referenceRecordUrls = []
        let annotation = null
        let detailPrompt = ''
        if (isStyleStep) {
          const applicableItems = this.referenceItemsForSpace(this.refineDraft.space)
          if (!applicableItems.length) throw new Error(`请先添加适用于“${this.refineDraft.space}”的风格参考图`)

          let constraintImageUrls = this.refineDraft.constraintImageUrls || []
          if (!constraintImageUrls.length) {
            const [contextFile, targetFile] = await this.createConstraintFiles()
            const constraintAssets = await Promise.all([
              this.uploadAsset(this.floorPlanFile),
              this.uploadAsset(contextFile),
              this.uploadAsset(targetFile)
            ])
            constraintImageUrls = constraintAssets.map(asset => asset.url)
            floorPlanRecordUrls = constraintAssets.map(asset => asset.localUrl).filter(Boolean)
          }
          if (!floorPlanRecordUrls.length) {
            floorPlanRecordUrls = this.refineDraft.inputContext?.floorPlanImages?.map(image => image.url) || []
          }
          const referenceAssets = await this.uploadReferenceImages(applicableItems)
          const referenceImageUrls = referenceAssets.map(asset => asset.url)
          referenceRecordUrls = referenceAssets.map(asset => asset.localUrl).filter(Boolean)
          annotation = this.refineDraft.inputContext?.annotation || this.currentAnnotation()
          supportingImageUrls = [...constraintImageUrls, ...referenceImageUrls]
          detailPrompt = this.buildStylePrompt(detailRequirement, {
            space: this.refineDraft.space,
            style: this.refineDraft.style,
            layoutNote: this.localLayoutNote,
            ratio: this.refineDraft.size || this.selectedRatio
          })
        } else {
          detailPrompt = `${FLOORPLAN_MODE_MARKER}
第1张图片是用户选中的效果图。本轮只做细节精修，不再改变其户型、镜头和空间布局。
本轮精修要求：${detailRequirement}。
必须继续保留当前镜头、户型结构、墙体、门窗、开口和主要家具布局，不得重新构图。`
        }
        const batch = await this.request('/api/generations/refine', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sourceTaskId: this.refineDraft.sourceTaskId,
            prompt: detailPrompt,
            space: this.refineDraft.space,
            style: this.refineDraft.style,
            size: this.refineDraft.size || this.selectedRatio,
            resolution: this.selectedResolution,
            quality: this.selectedQuality,
            count: 1,
            imageUrls: supportingImageUrls,
            stage: isStyleStep ? 'style' : 'detail',
            floorPlanRecordUrls,
            referenceRecordUrls,
            annotation,
            requestId
          }),
          timeoutMs: 120000,
          retries: 1,
          timeoutMessage: '创建精修任务等待超时，正在确认是否已成功提交'
        })
        const returnedTasks = (batch.tasks || []).filter(item => item && item.id)
        const taskIds = returnedTasks.map(item => item.id)
        if (taskIds.length !== 1) throw new Error(`${isStyleStep ? '风格效果图' : '精修'}任务创建不完整，请重试`)
        const resultStage = isStyleStep ? 'style' : 'detail'
        const record = {
          id: batch.batchId || `floorplan-${resultStage}-${Date.now()}`,
          space: this.refineDraft.space,
          style: this.refineDraft.style,
          size: this.refineDraft.size || this.selectedRatio,
          time: isStyleStep ? '风格效果图刚刚创建' : '精细版刚刚创建',
          prompt: detailRequirement,
          technicalPrompt: detailPrompt,
          constraintImageUrls: isStyleStep ? supportingImageUrls.slice(0, 3) : [],
          inputContext: isStyleStep
            ? this.inputContextFromVisualContext({ floorPlanRecordUrls, referenceRecordUrls, annotation })
            : this.normalizeInputContext(this.refineDraft.inputContext),
          images: Array(1).fill(''),
          taskIds,
          taskStates: returnedTasks.map(item => ({ status: item.status || 'pending', progress: Number(item.progress || 0) })),
          status: 'generating',
          stage: resultStage,
          parentBatchId: batch.parentBatchId || this.refineDraft.recordId,
          sourceTaskId: batch.sourceTaskId || this.refineDraft.sourceTaskId,
          progress: 0,
          error: ''
        }
        currentRecord = record
        this.records.unshift(record)
        this.refineDialogVisible = false
        await this.pollTasks(taskIds, record)
        if (this.deletedRecordIds.includes(record.id)) return
        if (this.completedImageCount(record) !== 1) {
          throw new Error(`${isStyleStep ? '风格效果图' : '精细版图片'}未返回，请重试`)
        }
        this.finishRecord(record)
        this.showToast(isStyleStep ? '1 张风格效果图已生成' : '1 张精细版已生成')
      } catch (error) {
        if (error.deleted) return
        if (currentRecord && error.pending) {
          currentRecord.status = 'generating'
          currentRecord.time = '任务仍在后台处理，请稍后刷新查看'
          this.showToast(error.message)
        } else if (currentRecord) this.failRecord(currentRecord, error)
        else this.showToast(error.message || `${isStyleStep ? '套用风格' : '精修'}失败，请稍后重试`)
      } finally {
        this.refining = false
      }
    },
    openImageViewer(image, record, index) {
      this.viewerScale = 1
      this.viewerDragging = false
      this.viewerPointerId = null
      this.imageViewer = {
        open: true,
        src: image,
        title: `${record.space} · ${record.style} · 方案 ${String(index + 1).padStart(2, '0')}`
      }
      document.body.classList.add('viewer-open')
      this.$nextTick(() => {
        if (this.$refs.imageViewer) this.$refs.imageViewer.focus()
        this.resetViewerPosition()
      })
    },
    openRecordInputImage(image, record) {
      this.viewerScale = 1
      this.viewerDragging = false
      this.viewerPointerId = null
      this.imageViewer = {
        open: true,
        src: image.url,
        title: `${record.space} · ${image.label}`
      }
      document.body.classList.add('viewer-open')
      this.$nextTick(() => {
        if (this.$refs.imageViewer) this.$refs.imageViewer.focus()
        this.resetViewerPosition()
      })
    },
    closeImageViewer() {
      this.endImageDrag()
      this.imageViewer = { open: false, src: '', title: '' }
      this.viewerScale = 1
      document.body.classList.remove('viewer-open')
    },
    zoomImage(delta) {
      this.viewerScale = Math.min(4, Math.max(0.5, Number((this.viewerScale + delta).toFixed(2))))
    },
    resetImageZoom() {
      this.viewerScale = 1
      this.resetViewerPosition()
    },
    handleViewerWheel(event) {
      this.zoomImage(event.deltaY < 0 ? 0.25 : -0.25)
    },
    startImageDrag(event) {
      if (event.target.tagName !== 'IMG') return
      if (event.pointerType === 'mouse' && event.button !== 0) return
      const stage = this.$refs.viewerStage
      if (!stage) return
      this.viewerDragging = true
      this.viewerPointerId = event.pointerId
      this.viewerDragStartX = event.clientX
      this.viewerDragStartY = event.clientY
      this.viewerDragScrollLeft = stage.scrollLeft
      this.viewerDragScrollTop = stage.scrollTop
      if (stage.setPointerCapture) stage.setPointerCapture(event.pointerId)
      event.preventDefault()
    },
    moveImageDrag(event) {
      if (!this.viewerDragging || event.pointerId !== this.viewerPointerId) return
      const stage = this.$refs.viewerStage
      if (!stage) return
      stage.scrollLeft = this.viewerDragScrollLeft - (event.clientX - this.viewerDragStartX)
      stage.scrollTop = this.viewerDragScrollTop - (event.clientY - this.viewerDragStartY)
      event.preventDefault()
    },
    endImageDrag(event) {
      if (event && this.viewerPointerId !== null && event.pointerId !== this.viewerPointerId) return
      const stage = this.$refs.viewerStage
      const pointerId = this.viewerPointerId
      if (stage && pointerId !== null && stage.hasPointerCapture && stage.hasPointerCapture(pointerId)) {
        stage.releasePointerCapture(pointerId)
      }
      this.viewerDragging = false
      this.viewerPointerId = null
    },
    resetViewerPosition() {
      this.$nextTick(() => {
        const stage = this.$refs.viewerStage
        if (!stage) return
        stage.scrollLeft = 0
        stage.scrollTop = 0
      })
    },
    async downloadImage(image, record, index) {
      try {
        const response = await fetch(image)
        if (!response.ok) throw new Error('图片下载失败')
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${record.space}-${record.style}-户型方案${String(index + 1).padStart(2, '0')}.jpg`
        document.body.appendChild(link)
        link.click()
        link.remove()
        URL.revokeObjectURL(url)
      } catch (error) {
        this.showToast(error.message || '图片下载失败')
      }
    }
  }
}
</script>

<style>
.workspace { width: min(1380px, calc(100% - 5vw)); margin: 34px auto 76px; }
.composer-card { background: #fff; border: 1px solid #e0e6e2; border-radius: 8px; box-shadow: 0 16px 48px rgba(39,57,47,.075); overflow: hidden; }
.composer-head { display: flex; justify-content: space-between; align-items: flex-start; padding: 30px 34px 24px; }
.step-label { display: inline-block; margin-bottom: 7px; color: var(--green); font-size: 11px; font-weight: 700; letter-spacing: 1px; }
.composer-head h2 { margin: 0; font-size: 21px; line-height: 1.35; font-weight: 600; }
.text-button { border: 0; background: none; color: #8a928d; font-size: 13px; padding: 6px; }
.text-button:hover { color: var(--green); }
.composer-body { display: grid; grid-template-columns: 35% minmax(0, 1fr); gap: 32px; padding: 0 34px 28px; }
.upload-box { min-height: 320px; border: 1.5px dashed #c7d1ca; border-radius: 6px; background: linear-gradient(145deg, #fbfcfb, #f7faf8); position: relative; overflow: hidden; cursor: pointer; display: grid; place-items: center; transition: .2s; }
.upload-box:hover { border-color: #8aaaf1; background: #f7faff; }
.upload-box input { display: none; }
.upload-content { max-width: 320px; display: flex; flex-direction: column; align-items: center; gap: 9px; padding: 30px; text-align: center; }
.upload-icon { width: 54px; height: 54px; display: grid; place-items: center; border-radius: 50%; background: var(--green-light); margin-bottom: 7px; }
.upload-icon svg { width: 24px; fill: none; stroke: var(--green); stroke-width: 1.7; }
.upload-content strong { font-size: 15px; }
.upload-content span { color: #929c96; font-size: 11px; line-height: 1.7; }
.upload-box img { width: 100%; height: 100%; position: absolute; object-fit: contain; background: #f4f4f2; }
.replace-file { position: absolute; right: 12px; bottom: 12px; border: 0; border-radius: 4px; padding: 8px 12px; background: rgba(23,33,28,.78); color: white; font-size: 12px; }
.form-panel { min-width: 0; display: flex; flex-direction: column; justify-content: flex-start; overflow: hidden; border: 1px solid #e5eae6; border-radius: 6px; background: #fff; }
.field-group { padding: 19px 22px; }
.field-group + .field-group { border-top: 1px solid #edf0ee; }
.field-title { display: flex; align-items: center; gap: 9px; font-size: 14px; font-weight: 600; margin-bottom: 13px; }
.field-title > span { width: 24px; height: 24px; display: inline-grid; place-items: center; flex: 0 0 24px; border-radius: 50%; background: #eef4ff; color: var(--green); font-size: 9px; font-weight: 700; }
.field-title em { font-style: normal; color: #dc2626; font-weight: 600; font-size: 11px; }
.space-groups { display: flex; flex-direction: column; gap: 14px; }
.space-group { display: grid; grid-template-columns: 62px minmax(0, 1fr); align-items: start; gap: 10px; }
.space-group-label { padding-top: 11px; color: #8a948d; font-size: 10px; line-height: 1.4; }
.chips { display: flex; flex-wrap: wrap; gap: 9px; }
.chips button { min-height: 39px; border: 1px solid #dfe5e1; border-radius: 4px; background: #fff; color: #536059; padding: 0 16px; font-size: 12px; transition: .18s; }
.chips button:hover { border-color: #9bb5ee; }
.chips button.selected { border-color: var(--green); background: var(--green-light); color: var(--green); font-weight: 600; box-shadow: inset 0 0 0 1px var(--green), 0 4px 10px rgba(37,99,235,.08); }
.reference-field { min-width: 0; }
.reference-toolbar { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.reference-toolbar > div { min-width: 0; }
.reference-toolbar .field-title { margin-bottom: 7px; }
.reference-toolbar p { margin: 0; color: #7e8881; font-size: 11px; line-height: 1.65; }
.reference-count { flex: 0 0 auto; border: 1px solid #d9e3f5; border-radius: 4px; padding: 4px 7px; background: #f6f9ff; color: #526f9f; font-size: 9px; font-weight: 700; font-variant-numeric: tabular-nums; }
.reference-empty { min-height: 84px; display: flex; align-items: center; gap: 14px; margin-top: 15px; border: 1px dashed #b9c8e4; border-radius: 5px; padding: 13px 18px; background: #f8faff; cursor: pointer; transition: border-color .18s ease, background .18s ease, box-shadow .18s ease; }
.reference-empty:hover { border-color: #5e88df; background: #f2f6ff; box-shadow: inset 0 0 0 1px rgba(37,99,235,.08); }
.reference-empty input, .reference-add-card input { display: none; }
.reference-empty-icon { width: 44px; height: 44px; flex: 0 0 44px; display: grid; place-items: center; position: relative; border: 1px solid #cddbf5; border-radius: 6px; background: #fff; color: #3970da; font-size: 20px; box-shadow: 0 4px 12px rgba(37,99,235,.08); }
.reference-empty-icon .el-icon-plus { width: 17px; height: 17px; display: grid; place-items: center; position: absolute; right: -5px; bottom: -5px; border: 2px solid #f8faff; border-radius: 50%; background: #2563eb; color: #fff; font-size: 9px; }
.reference-empty-copy { min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.reference-empty-copy strong { color: #2e3b34; font-size: 13px; }
.reference-empty-copy small { color: #8b9690; font-size: 10px; line-height: 1.55; }
.reference-upload-icon { margin-left: auto; color: #7f9ed9; font-size: 18px; }
.reference-status { min-height: 36px; display: flex; align-items: center; gap: 8px; margin-top: 12px; padding: 9px 11px; border-left: 2px solid #d7ddd8; border-radius: 0 5px 5px 0; background: #f8faf8; color: #7e8881; font-size: 10px; line-height: 1.5; }
.reference-status.active { border-color: #c5d8f8; background: #f3f7ff; color: #315fba; }
.reference-status i { flex: 0 0 auto; font-size: 13px; }
.reference-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(138px, 168px)); gap: 10px; margin-top: 12px; }
.reference-card { width: 100%; max-width: 168px; min-width: 0; overflow: hidden; border: 1px solid #dfe5e0; border-radius: 6px; background: #fff; transition: border-color .18s ease, box-shadow .18s ease, transform .18s ease; }
.reference-card:hover { border-color: #b7c8e7; box-shadow: 0 7px 18px rgba(39,57,47,.08); transform: translateY(-1px); }
.reference-preview { width: 100%; height: 104px; max-height: 104px; position: relative; overflow: hidden; background: #eef1ee; }
.reference-preview img { width: 100%; height: 100%; display: block; object-fit: cover; }
.reference-preview > button { width: 26px; height: 26px; position: absolute; top: 7px; right: 7px; display: grid; place-items: center; border: 1px solid rgba(255,255,255,.28); border-radius: 4px; background: rgba(20,31,25,.76); color: #fff; opacity: 0; transform: translateY(-2px); transition: .18s; }
.reference-preview:hover > button, .reference-preview > button:focus-visible { opacity: 1; transform: none; }
.reference-card-footer { display: flex; flex-direction: column; align-items: stretch; gap: 5px; padding: 8px; }
.reference-card-footer small { color: #8a948d; font-size: 8px; line-height: 1; }
.reference-card-footer .el-select { width: 100%; }
.reference-card-footer .el-input__inner { height: 28px; border-color: #dfe5e0; border-radius: 4px; padding-left: 8px; color: #445149; font-size: 10px; }
.reference-card-footer .el-input__icon { line-height: 28px; }
.reference-add-card { width: 100%; max-width: 168px; min-height: 153px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 5px; border: 1px dashed #c7d3c9; border-radius: 6px; background: #fafbfa; color: #67736c; cursor: pointer; transition: border-color .18s ease, background .18s ease, color .18s ease; }
.reference-add-card:hover { border-color: #7396dd; background: #f5f8ff; color: #245cc5; }
.reference-add-card > span { width: 30px; height: 30px; display: grid; place-items: center; margin-bottom: 2px; border-radius: 50%; background: #edf2ee; color: #5f6d64; font-size: 13px; transition: .18s; }
.reference-add-card:hover > span { background: #e8f0ff; color: #2563eb; }
.reference-add-card strong { font-size: 10px; }
.reference-add-card small { color: #9aa29d; font-size: 8px; }
.ai-prompt-panel { margin: 0 34px 28px; padding: 22px; border: 1px solid #b9cffb; border-left: 4px solid var(--green); border-radius: 6px; background: linear-gradient(135deg, #f5f9ff 0%, #fff 72%); box-shadow: 0 10px 26px rgba(37,99,235,.08); }
.prompt-heading { display: flex; align-items: center; gap: 13px; }
.ai-avatar { width: 40px; height: 40px; display: grid; place-items: center; flex: 0 0 40px; border-radius: 5px; background: linear-gradient(135deg, #2563eb, #4f8df7); color: white; font-size: 11px; font-weight: 700; box-shadow: 0 5px 12px rgba(37,99,235,.2); }
.prompt-heading > div:nth-child(2) { display: flex; flex-direction: column; gap: 4px; }
.prompt-heading strong { font-size: 15px; color: #172b4d; }
.required-label { margin-left: 5px; color: #dc2626; font-size: 11px; font-style: normal; font-weight: 600; }
.prompt-heading span { color: #7a8ba8; font-size: 11px; line-height: 1.5; }
.prompt-heading em { margin-left: auto; color: #9aa8bd; font-size: 10px; font-style: normal; }
.prompt-row { margin-top: 16px; border: 1px solid #cedbf3; border-radius: 5px; background: #fff; display: flex; align-items: flex-end; padding: 10px; transition: .2s; }
.prompt-row:focus-within { border-color: #5b8def; box-shadow: 0 0 0 3px rgba(37,99,235,.1); }
.prompt-row textarea { width: 100%; height: 96px; padding: 11px 12px; resize: none; border: 0; outline: 0; background: transparent; color: var(--ink); font-size: 13px; line-height: 1.75; }
.prompt-row textarea::placeholder { color: #a0aec0; }
.prompt-actions { flex: 0 0 132px; display: flex; flex-direction: column; gap: 10px; }
.prompt-action { position: relative; }
.magic-button { width: 100%; height: 42px; display: flex; align-items: center; justify-content: flex-start; gap: 8px; border: 1px solid #c7d8fb; border-radius: 5px; padding: 0 13px; color: #1d4ed8; background: #eff6ff; font-size: 11px; font-weight: 600; white-space: nowrap; transition: border-color .18s ease, background .18s ease, color .18s ease, box-shadow .18s ease, transform .18s ease; }
.magic-button > i { width: 16px; flex: 0 0 16px; font-size: 15px; text-align: center; }
.magic-button:hover:not(:disabled) { transform: translateY(-1px); }
.magic-button:focus-visible { outline: 0; box-shadow: 0 0 0 3px rgba(37,99,235,.16); }
.magic-button:disabled { cursor: wait; opacity: .68; }
.write-button { border-color: #2563eb; background: #2563eb; color: #fff; box-shadow: 0 4px 10px rgba(37,99,235,.18); }
.write-button:hover:not(:disabled) { border-color: #1d4ed8; background: #1d4ed8; box-shadow: 0 7px 16px rgba(37,99,235,.24); }
.write-button .magic-spinner { border-color: rgba(255,255,255,.35); border-top-color: #fff; }
.enhance-button { border-color: #d6deea; background: #fff; color: #43536d; box-shadow: 0 2px 6px rgba(31,52,77,.04); }
.enhance-button:hover:not(:disabled) { border-color: #8ba9d9; background: #f7faff; color: #1f5bb8; box-shadow: 0 6px 14px rgba(41,85,145,.1); }
.action-tooltip { width: 246px; min-height: 82px; position: absolute; z-index: 8; right: calc(100% + 12px); top: 50%; display: flex; align-items: flex-start; gap: 11px; padding: 13px; border: 1px solid #dbe3ef; border-radius: 6px; background: rgba(255,255,255,.98); box-shadow: 0 14px 34px rgba(31,48,71,.16), 0 3px 10px rgba(31,48,71,.08); pointer-events: none; opacity: 0; visibility: hidden; transform: translate(6px, -50%); transition: opacity .16s ease, transform .16s ease, visibility .16s; }
.action-tooltip::before { content: ''; position: absolute; right: -6px; top: calc(50% - 6px); width: 11px; height: 11px; border-top: 1px solid #dbe3ef; border-right: 1px solid #dbe3ef; background: #fff; transform: rotate(45deg); }
.prompt-action:hover .action-tooltip, .prompt-action:focus-within .action-tooltip { opacity: 1; visibility: visible; transform: translate(0, -50%); }
.tooltip-icon { width: 32px; height: 32px; display: grid; place-items: center; flex: 0 0 32px; border-radius: 5px; background: #f0f4f9; color: #58708e; font-size: 15px; }
.tooltip-icon-primary { background: #eaf2ff; color: #2563eb; }
.tooltip-copy { min-width: 0; display: flex; flex-direction: column; align-items: flex-start; }
.tooltip-copy strong { color: #25344a; font-size: 12px; line-height: 1.4; }
.tooltip-copy small { margin-top: 3px; color: #718096; font-size: 10px; line-height: 1.55; white-space: normal; }
.tooltip-copy em { margin-top: 7px; padding: 3px 6px; border-radius: 3px; background: #f2f5f8; color: #68778a; font-size: 9px; font-style: normal; line-height: 1; }
.magic-spinner { width: 13px; height: 13px; display: inline-block; border: 2px solid #b8cdf7; border-top-color: #2563eb; border-radius: 50%; animation: spin .75s linear infinite; vertical-align: -2px; }
.prompt-suggestions { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-top: 14px; }
.prompt-suggestions > span { margin-right: 2px; color: #8b98aa; font-size: 10px; }
.prompt-suggestions button { border: 1px solid #dce6f8; border-radius: 4px; background: rgba(255,255,255,.78); color: #56709d; padding: 6px 10px; font-size: 10px; }
.prompt-suggestions button:hover { border-color: #9bb8f2; color: #2563eb; background: #fff; }
.prompt-suggestions .preset-toggle { margin-left: auto; display: inline-flex; align-items: center; gap: 5px; border-color: #c7d8fb; background: #eff6ff; color: #2563eb; font-weight: 600; }
.prompt-suggestions .preset-toggle:hover,
.prompt-suggestions .preset-toggle.active { border-color: #2563eb; background: #2563eb; color: #fff; }
.prompt-presets { margin-top: 16px; overflow: hidden; border: 1px solid #d6e2f5; border-radius: 7px; background: rgba(255,255,255,.88); box-shadow: 0 8px 22px rgba(37,99,235,.07); }
.preset-intro { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 14px 16px; border-bottom: 1px solid #e3eaf5; background: linear-gradient(90deg, #f1f6ff, #f9fbff); }
.preset-intro > div { min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.preset-intro strong { color: #20345a; font-size: 12px; }
.preset-intro span { color: #7485a2; font-size: 10px; line-height: 1.5; }
.preset-intro .preset-live { flex: 0 0 auto; display: inline-flex; align-items: center; gap: 6px; border: 1px solid #b9d3c1; border-radius: 999px; padding: 4px 8px; background: #f2fbf4; color: #33734a; font-size: 9px; }
.preset-live i { width: 6px; height: 6px; border-radius: 50%; background: #36a35b; box-shadow: 0 0 0 3px rgba(54,163,91,.12); }
.preset-state { min-height: 76px; display: flex; align-items: center; justify-content: center; gap: 8px; padding: 18px; color: #73829a; font-size: 11px; }
.preset-state.preset-error { color: #b54848; }
.preset-error button { margin-left: 3px; border-color: #e7bcbc; background: #fff8f8; color: #b54848; }
.preset-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; padding: 14px; }
.preset-card { min-width: 0; overflow: hidden; border: 1px solid #e0e7f1; border-radius: 6px; background: #fff; }
.preset-card header { min-height: 55px; display: grid; grid-template-columns: 28px minmax(0, 1fr) auto; align-items: center; gap: 9px; padding: 11px 12px; border-bottom: 1px solid #edf1f6; background: #fbfcfe; }
.preset-card header > span { width: 27px; height: 27px; display: grid; place-items: center; border-radius: 5px; background: #eaf2ff; color: #2563eb; font-size: 9px; font-weight: 700; }
.preset-card header > div { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.preset-card header strong { color: #263a5c; font-size: 11px; }
.preset-card header small { overflow: hidden; color: #8491a5; font-size: 9px; text-overflow: ellipsis; white-space: nowrap; }
.preset-card header em { border-radius: 3px; padding: 4px 6px; background: #eef2f7; color: #64748b; font-size: 8px; font-style: normal; white-space: nowrap; }
.preset-block { padding: 12px; }
.preset-block label { display: block; margin-bottom: 7px; color: #536581; font-size: 9px; font-weight: 700; }
.preset-block pre { max-height: 260px; margin: 0; overflow: auto; border: 1px solid #e5eaf1; border-radius: 5px; padding: 11px; background: #f7f9fc; color: #3d4d63; font-family: "Microsoft YaHei", sans-serif; font-size: 10px; line-height: 1.7; white-space: pre-wrap; word-break: break-word; }
.preset-reveal-enter-active, .preset-reveal-leave-active { transition: opacity .2s ease, transform .2s ease; transform-origin: top; }
.preset-reveal-enter, .preset-reveal-leave-to { opacity: 0; transform: translateY(-6px); }
.composer-footer { border-top: 1px solid #edf0ed; padding: 16px 30px; display: flex; align-items: center; justify-content: space-between; gap: 20px; }
.settings-row { display: flex; gap: 8px; flex-wrap: wrap; }
.setting-pill { border: 1px solid #dfe4e0; background: #fff; border-radius: 4px; padding: 8px 11px; color: #5a655e; font-size: 11px; display: flex; gap: 7px; align-items: center; }
.setting-pill b { color: #969e98; font-weight: 400; }
.setting-pill:focus-visible { outline: 0; border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.12); }
.dot { width: 9px; height: 9px; border-radius: 50%; display: inline-block; }
.ai-dot { background: linear-gradient(135deg, #2563eb, #38bdf8); }
.grid-icon { font-size: 16px; line-height: 10px; }
.generate-button { min-width: 168px; height: 45px; display: flex; align-items: center; justify-content: center; gap: 8px; border: 0; border-radius: 5px; background: var(--green); color: #fff; padding: 0 18px; font-size: 13px; font-weight: 600; box-shadow: 0 7px 16px rgba(37,99,235,.22); }
.generate-button:hover { background: var(--green-dark); }
.generate-button:disabled { opacity: .85; cursor: wait; }
.generate-button small { opacity: .65; font-size: 10px; font-weight: 400; padding-left: 4px; }
.spinner { width: 15px; height: 15px; border: 2px solid rgba(255,255,255,.3); border-top-color: white; border-radius: 50%; animation: spin .75s linear infinite; }
.progress-track { height: 3px; background: #eaf0ec; }
.progress-track i { display: block; height: 100%; background: linear-gradient(90deg, var(--green), #60a5fa); transition: width .25s ease; }
@keyframes spin { to { transform: rotate(360deg); } }

.history-section { margin-top: 34px; }
.section-head { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 15px; padding: 0 2px; }
.section-head h2 { margin: 0 0 4px; font-size: 18px; }
.section-head p { margin: 0; color: #8b948e; font-size: 12px; }
.filter-tabs { padding: 3px; background: #e9ede9; border-radius: 5px; }
.filter-tabs button { border: 0; background: transparent; color: #79827c; border-radius: 3px; padding: 7px 13px; font-size: 11px; }
.filter-tabs button.active { color: #29352e; background: #fff; box-shadow: 0 2px 6px rgba(29,40,33,.08); }
.record-card { background: #fff; border: 1px solid #e1e6e1; border-radius: 8px; padding: 20px; box-shadow: 0 7px 25px rgba(38,55,45,.04); }
.record-card + .record-card { margin-top: 14px; }
.record-generating { border-color: #bcd0f8; box-shadow: 0 8px 28px rgba(37,99,235,.07); }
.record-failed { border-color: #f1c8c8; }
.record-meta { display: flex; align-items: center; justify-content: space-between; color: #939b95; font-size: 11px; margin-bottom: 15px; }
.record-title { display: flex; align-items: center; gap: 8px; }
.record-title strong { color: #29352e; font-size: 13px; }
.record-meta-actions { min-width: 0; display: flex; align-items: center; justify-content: flex-end; gap: 10px; }
.record-meta-actions > span { overflow-wrap: anywhere; text-align: right; }
.record-meta-actions b { margin-right: 5px; color: #657169; font-size: 10px; }
.record-meta-actions time { color: #7d8781; font-variant-numeric: tabular-nums; }
.record-delete-button { width: 30px; height: 30px; flex: 0 0 30px; display: grid; place-items: center; border: 1px solid #e1e5e2; border-radius: 4px; background: #fff; color: #89928c; cursor: pointer; transition: .16s; }
.record-delete-button:hover:not(:disabled) { border-color: #e7b3b3; background: #fff5f5; color: #c43d3d; }
.record-delete-button:focus-visible { outline: 2px solid #dc4949; outline-offset: 2px; }
.record-delete-button:disabled { cursor: wait; opacity: .6; }
.stage-tag { border: 1px solid #d9e1dc; border-radius: 3px; padding: 3px 7px; background: #f7f9f7; color: #68736c; font-size: 9px; font-weight: 700; }
.ratio-tag { display: inline-flex; align-items: center; gap: 5px; border: 1px solid #d9e2ef; border-radius: 3px; padding: 3px 7px; background: #f7f9fc; color: #536581; font-size: 9px; font-weight: 700; }
.ratio-tag i { display: inline-block; border: 1px solid currentColor; border-radius: 1px; }
.stage-detail { border-color: #d8c7f2; background: #f8f3ff; color: #6842a3; }
.status-dot { width: 7px; height: 7px; border-radius: 50%; background: #3b82f6; box-shadow: 0 0 0 4px #eaf2ff; margin-right: 3px; }
.dot-generating { animation: statusPulse 1.4s ease-in-out infinite; }
.dot-completed { background: #3b82f6; }
.dot-failed { background: #dc4949; box-shadow: 0 0 0 4px #fff0f0; }
.success-tag { color: #1d4ed8; background: #eff6ff; border-radius: 3px; padding: 3px 7px; font-size: 10px; }
.tag-generating { color: #1d4ed8; background: #eaf2ff; }
.tag-failed { color: #b42323; background: #fff0f0; }
.image-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 11px; }
.image-grid.two-up { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.image-grid.two-up .result-image,
.image-grid.two-up .loading-visual,
.image-grid.two-up .failed-visual { height: 260px; }
.result-card { min-width: 0; border: 1px solid #e8ebe8; border-radius: 5px; overflow: hidden; }
.loading-result { border-color: #dce7fa; background: #f7faff; }
.failed-slot { border-color: #f1c8c8; background: #fff8f8; }
.loading-visual { height: 170px; display: flex; flex-direction: column; align-items: center; justify-content: center; position: relative; overflow: hidden; color: #5c7197; }
.loading-visual::before { content: ''; position: absolute; inset: 0; background: linear-gradient(105deg, transparent 30%, rgba(255,255,255,.85) 48%, transparent 66%); transform: translateX(-100%); animation: shimmer 1.8s infinite; }
.loading-visual strong { margin: 9px 0 3px; font-size: 11px; }
.loading-visual small { color: #8b9bb5; font-size: 9px; }
.loading-spinner { width: 22px; height: 22px; border: 2px solid #d6e3fa; border-top-color: #2563eb; border-radius: 50%; animation: spin .8s linear infinite; }
.failed-visual { height: 170px; display: flex; flex-direction: column; align-items: center; justify-content: center; color: #987575; }
.failed-visual > span { width: 28px; height: 28px; display: grid; place-items: center; border-radius: 50%; background: #fee9e9; color: #c23333; font-weight: 700; }
.failed-visual strong { margin: 9px 0 3px; font-size: 11px; }
.failed-visual small { max-width: 82%; font-size: 9px; line-height: 1.55; text-align: center; }
.result-image { height: 170px; background-color: #eef1ee; background-size: cover; background-position: center; background-repeat: no-repeat; position: relative; cursor: zoom-in; outline: 0; }
.result-image:focus-visible { box-shadow: inset 0 0 0 3px #60a5fa; }
.choice-badge { position: absolute; left: 9px; top: 9px; background: rgba(255,255,255,.9); color: var(--green); border-radius: 3px; padding: 4px 7px; font-size: 9px; font-weight: 700; }
.image-actions { position: absolute; right: 8px; top: 8px; display: flex; gap: 5px; opacity: 0; transform: translateY(-3px); transition: .2s; }
.result-image:hover .image-actions { opacity: 1; transform: none; }
.image-actions button { width: 28px; height: 28px; border: 0; border-radius: 4px; background: rgba(23,33,28,.78); color: #fff; }
.result-caption { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 6px; padding: 9px 10px; font-size: 10px; color: #7d867f; }
.result-caption em { color: #3970da; font-style: normal; font-size: 9px; }
.result-caption button { border: 0; background: none; color: var(--green); font-size: 10px; }
.result-caption .workflow-tuning-button { width: auto; margin-top: 2px; }
.record-inputs { margin-top: 15px; border-top: 1px solid #edf0ed; padding-top: 14px; }
.record-inputs-head { display: flex; align-items: baseline; gap: 9px; margin-bottom: 12px; }
.record-inputs-head strong { color: #344039; font-size: 12px; }
.record-inputs-head span { color: #929a94; font-size: 10px; }
.record-input-groups { display: flex; align-items: flex-start; gap: 28px; }
.record-input-group { min-width: 0; }
.record-input-group > span { display: block; margin-bottom: 7px; color: #727c75; font-size: 10px; font-weight: 700; }
.record-input-images { display: flex; flex-wrap: wrap; gap: 8px; }
.record-input-images button { width: 104px; padding: 0; overflow: hidden; border: 1px solid #dfe5e1; border-radius: 5px; background: #fff; color: #657069; text-align: left; cursor: zoom-in; }
.record-input-images button:hover { border-color: #87a7ed; box-shadow: 0 3px 10px rgba(37,99,235,.1); }
.record-input-images button:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; }
.record-input-images img { display: block; width: 100%; height: 72px; object-fit: contain; background: #f1f3f1; }
.record-input-images small { display: block; padding: 6px 7px; overflow: hidden; font-size: 9px; line-height: 1.2; text-overflow: ellipsis; white-space: nowrap; }
.record-annotation { display: flex; align-items: flex-start; flex-wrap: wrap; gap: 6px 16px; margin-top: 11px; color: #61708a; font-size: 9px; line-height: 1.6; }
.record-annotation span { min-width: 0; display: inline-flex; align-items: flex-start; gap: 5px; overflow-wrap: anywhere; }
.record-annotation i { flex: 0 0 auto; margin-top: 2px; color: #3970da; }
.record-inputs-unavailable { margin-top: 15px; border-top: 1px solid #edf0ed; padding-top: 12px; color: #969e98; font-size: 10px; }
.record-inputs-unavailable i { margin-right: 4px; }
.record-footer { border-top: 1px solid #edf0ed; margin-top: 15px; padding-top: 14px; display: flex; justify-content: space-between; align-items: center; gap: 20px; }
.record-prompt { width: fit-content; max-width: min(760px, 100%); min-width: 0; flex: 1 1 auto; margin: 0; color: #7f8882; font-size: 11px; line-height: 1.65; overflow-wrap: anywhere; white-space: normal; }
.record-prompt span { color: #b1b7b3; }
.record-footer > div { flex: 0 0 auto; display: flex; gap: 8px; }
.record-footer button { border: 1px solid #dfe4e0; background: #fff; color: #667069; border-radius: 4px; padding: 7px 12px; font-size: 10px; white-space: nowrap; }
.record-footer .primary-outline { border-color: #8aaaf1; color: var(--green); }
.polling-hint { display: flex !important; align-items: center; gap: 7px; color: #60769e; font-size: 10px; white-space: nowrap; }
.polling-hint i { width: 6px; height: 6px; border-radius: 50%; background: #2563eb; animation: statusPulse 1.4s ease-in-out infinite; }
.failed-result { min-height: 116px; display: flex; align-items: center; justify-content: center; gap: 12px; background: #fff8f8; border: 1px dashed #edc5c5; color: #6d5555; }
.failed-result > span { width: 28px; height: 28px; display: grid; place-items: center; border-radius: 50%; background: #fee9e9; color: #c23333; font-weight: 700; }
.failed-result strong { font-size: 12px; }
.failed-result p { margin: 3px 0 0; color: #987575; font-size: 10px; }
.record-error-guidance { display: flex; align-items: center; gap: 10px; margin-top: 12px; border: 1px solid #efc7c7; border-radius: 6px; padding: 11px 13px; background: #fff7f7; color: #8d3f3f; }
.record-error-guidance > i { flex: 0 0 auto; font-size: 18px; }
.record-error-guidance > div { min-width: 0; flex: 1; display: flex; flex-direction: column; gap: 4px; }
.record-error-guidance strong { font-size: 10px; line-height: 1.5; overflow-wrap: anywhere; }
.record-error-guidance span { color: #9d6666; font-size: 9px; line-height: 1.55; overflow-wrap: anywhere; }
.record-error-guidance button { flex: 0 0 auto; border: 1px solid #dca7a7; border-radius: 4px; padding: 7px 11px; background: #fff; color: #a43c3c; font-size: 9px; }
.record-error-guidance button:disabled { cursor: wait; opacity: .55; }
.empty-record { height: 220px; background: #fff; border: 1px dashed #d6dcd7; border-radius: 8px; display: flex; flex-direction: column; align-items: center; justify-content: center; color: #959d97; gap: 7px; }
.empty-record strong { color: #566159; font-size: 13px; }
.empty-record span { font-size: 11px; }
.empty-icon { font-size: 21px; color: #6b93e8; }
.refine-dialog { border-radius: 8px; overflow: hidden; }
.refine-dialog .el-dialog__header { padding: 20px 22px 16px; border-bottom: 1px solid #e9edea; }
.refine-dialog .el-dialog__title { color: #26322b; font-size: 16px; font-weight: 700; }
.refine-dialog .el-dialog__body { padding: 22px; }
.refine-dialog .el-dialog__footer { padding: 15px 22px; border-top: 1px solid #e9edea; background: #fafbfa; }
.refine-workspace { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr); gap: 20px; }
.refine-preview { min-width: 0; position: relative; overflow: hidden; border: 1px solid #dfe5e0; border-radius: 6px; background: #edf1ee; aspect-ratio: 4 / 3; }
.refine-preview img { width: 100%; height: 100%; display: block; object-fit: cover; }
.refine-preview > span { max-width: calc(100% - 20px); position: absolute; left: 10px; bottom: 10px; overflow: hidden; border-radius: 3px; padding: 5px 8px; background: rgba(23,33,28,.82); color: #fff; font-size: 9px; text-overflow: ellipsis; white-space: nowrap; }
.refine-form { min-width: 0; display: flex; flex-direction: column; }
.refine-kicker { align-self: flex-start; border-radius: 3px; padding: 4px 7px; background: #edf4ff; color: #2563eb; font-size: 9px; font-weight: 700; }
.refine-form h3 { margin: 9px 0 12px; color: #26322b; font-size: 16px; }
.refine-form textarea { width: 100%; min-height: 128px; resize: vertical; border: 1px solid #d6ded8; border-radius: 5px; outline: 0; padding: 11px 12px; color: #334039; font-size: 11px; line-height: 1.65; }
.refine-form textarea:focus { border-color: #6692eb; box-shadow: 0 0 0 3px rgba(37,99,235,.1); }
.refine-form textarea::placeholder { color: #9aa39d; }
.refine-locks { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 12px; }
.refine-locks span { display: inline-flex; align-items: center; gap: 5px; border: 1px solid #e0e5e1; border-radius: 4px; padding: 6px 8px; background: #f8faf8; color: #68736c; font-size: 9px; }
.refine-locks i { color: #3970da; }
.refine-dialog .el-button { border-radius: 5px; }
.refine-dialog .el-button--primary { border-color: #2563eb; background: #2563eb; }

@media (max-width: 1000px) {
  .composer-body { grid-template-columns: 1fr; }
  .upload-box { min-height: 190px; }
  .reference-grid { grid-template-columns: repeat(auto-fill, minmax(138px, 168px)); }
  .image-grid { grid-template-columns: repeat(2, 1fr); }
  .image-grid.two-up .result-image,
  .image-grid.two-up .loading-visual,
  .image-grid.two-up .failed-visual { height: 220px; }
}

@media (max-width: 767px) {
  .workspace { width: calc(100% - 24px); margin-top: 15px; }
  .composer-head, .composer-body { padding-left: 17px; padding-right: 17px; }
  .composer-head h2 { font-size: 16px; }
  .composer-body { gap: 18px; }
  .space-group { grid-template-columns: 1fr; gap: 6px; }
  .space-group-label { padding-top: 0; }
  .reference-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .reference-card, .reference-add-card { max-width: none; }
  .reference-preview { height: 104px; max-height: 104px; }
  .reference-preview > button { opacity: 1; transform: none; }
  .ai-prompt-panel { margin-left: 17px; margin-right: 17px; padding: 14px; }
  .prompt-row { align-items: stretch; flex-direction: column; }
  .prompt-actions { flex: 0 0 auto; flex-direction: row; }
  .prompt-action { flex: 1 1 0; min-width: 0; }
  .magic-button { height: 36px; min-height: 36px !important; gap: 6px; padding: 0 9px; font-size: 11px !important; }
  .magic-button > i { font-size: 13px; }
  .prompt-suggestions .preset-toggle { margin-left: 0; }
  .preset-grid { grid-template-columns: 1fr; }
  .magic-button { justify-content: center; }
  .action-tooltip { display: none; }
  .composer-footer { align-items: stretch; flex-direction: column; padding: 15px 17px; }
  .generate-button { width: 100%; height: 40px; min-height: 40px !important; padding: 0 12px; font-size: 12px !important; box-shadow: 0 5px 12px rgba(37,99,235,.18); }
  .generate-button small { display: none; }
  .image-grid { grid-template-columns: 1fr; }
  .image-grid.two-up { grid-template-columns: 1fr; }
  .image-grid.two-up .result-image,
  .image-grid.two-up .loading-visual,
  .image-grid.two-up .failed-visual { height: 210px; }
  .result-image { height: 210px; }
  .record-meta, .record-footer { align-items: flex-start; flex-direction: column; }
  .record-title { flex-wrap: wrap; }
  .record-inputs-head { align-items: flex-start; flex-direction: column; gap: 3px; }
  .record-input-groups { flex-direction: column; gap: 14px; }
  .record-input-images button { width: 92px; }
  .record-input-images img { height: 64px; }
  .record-footer { width: 100%; min-width: 0; }
  .record-footer p { width: 100%; max-width: 100%; }
  .record-error-guidance { align-items: flex-start; flex-wrap: wrap; }
  .record-error-guidance > div { flex-basis: calc(100% - 30px); }
  .record-error-guidance button { margin-left: 28px; }
  .filter-tabs { display: none; }
  .refine-dialog { width: calc(100% - 20px) !important; margin-top: 5vh !important; }
  .refine-dialog .el-dialog__body { padding: 16px; }
  .refine-workspace { grid-template-columns: 1fr; gap: 15px; }
  .refine-preview { max-height: 260px; }
}

.floorplan-upload-column { min-width: 0; display: flex; flex-direction: column; gap: 10px; }
.floorplan-upload-box { min-height: 286px; }
.plan-constraint-summary { min-height: 62px; display: flex; align-items: center; justify-content: space-between; gap: 12px; border: 1px solid #e0e5e1; border-left: 3px solid #b8c0ba; border-radius: 5px; padding: 10px 12px; background: #fafbfa; }
.plan-constraint-summary.ready { border-color: #bcd0f8; border-left-color: #2563eb; background: #f6f9ff; }
.constraint-summary-main { min-width: 0; display: flex; align-items: center; gap: 9px; }
.constraint-state-icon { width: 30px; height: 30px; flex: 0 0 30px; display: grid; place-items: center; border-radius: 4px; background: #edf0ed; color: #77817a; }
.plan-constraint-summary.ready .constraint-state-icon { background: #e7efff; color: #2563eb; }
.constraint-summary-main > div { min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.constraint-summary-main strong { color: #344139; font-size: 11px; }
.constraint-summary-main small { overflow: hidden; color: #8a948d; font-size: 9px; text-overflow: ellipsis; white-space: nowrap; }
.plan-constraint-summary > button { flex: 0 0 auto; border: 1px solid #a9bde5; border-radius: 4px; padding: 7px 10px; background: #fff; color: #245cc5; font-size: 10px; }
.constraint-checkbar { min-height: 52px; display: flex; align-items: center; justify-content: center; gap: 12px; margin: 0 30px 4px; border-top: 1px solid #edf0ed; color: #88918b; }
.constraint-checkbar span { display: flex; align-items: center; gap: 6px; font-size: 10px; white-space: nowrap; }
.constraint-checkbar span i { width: 20px; height: 20px; display: grid; place-items: center; border-radius: 50%; background: #eef1ee; color: #7c867f; font-size: 9px; font-style: normal; font-weight: 700; }
.constraint-checkbar span.done { color: #315fba; }
.constraint-checkbar span.done i { background: #2563eb; color: #fff; }
.constraint-checkbar b { width: 44px; height: 1px; background: #dfe4e0; }
.constraint-pill { color: #1d4ed8; background: #f3f7ff; border-color: #c8d7f5; }
.floorplan-marking-dialog { border-radius: 8px; overflow: hidden; }
.floorplan-marking-dialog .el-dialog__header { padding: 20px 22px 16px; border-bottom: 1px solid #e9edea; }
.floorplan-marking-dialog .el-dialog__title { color: #26322b; font-size: 16px; font-weight: 700; }
.floorplan-marking-dialog .el-dialog__body { padding: 20px 22px; }
.floorplan-marking-dialog .el-dialog__footer { padding: 14px 22px; border-top: 1px solid #e9edea; background: #fafbfa; }
.marking-layout { display: grid; grid-template-columns: minmax(0, 1fr) 238px; gap: 20px; }
.marking-board { min-width: 0; }
.marking-board-toolbar { min-height: 48px; display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 10px; }
.marking-board-toolbar > div { display: flex; flex-direction: column; gap: 4px; }
.marking-board-toolbar strong { color: #303d35; font-size: 12px; }
.marking-board-toolbar span { color: #8a948d; font-size: 9px; }
.marking-board-toolbar button { border: 0; background: transparent; color: #66736b; font-size: 10px; }
.marking-canvas-shell { height: 500px; display: flex; align-items: center; justify-content: center; overflow: hidden; border: 1px solid #d9dedb; border-radius: 5px; background-color: #eef1ee; background-image: linear-gradient(45deg, #e6eae7 25%, transparent 25%), linear-gradient(-45deg, #e6eae7 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e6eae7 75%), linear-gradient(-45deg, transparent 75%, #e6eae7 75%); background-position: 0 0, 0 8px, 8px -8px, -8px 0; background-size: 16px 16px; }
.marking-image-wrap { max-width: 100%; max-height: 100%; position: relative; line-height: 0; cursor: crosshair; touch-action: none; user-select: none; }
.marking-image-wrap > img { max-width: 100%; max-height: 498px; display: block; object-fit: contain; pointer-events: none; user-select: none; }
.room-selection { position: absolute; border: 3px solid #2563eb; background: rgba(37,99,235,.14); box-shadow: 0 0 0 9999px rgba(22,29,25,.18); pointer-events: none; }
.room-selection > span { position: absolute; left: -3px; top: -27px; min-width: 58px; height: 24px; display: flex; align-items: center; justify-content: center; border-radius: 3px 3px 0 0; padding: 0 8px; background: #2563eb; color: #fff; font-size: 10px; line-height: 1; white-space: nowrap; }
.selection-arrow { position: absolute; left: 50%; top: 50%; color: #dc2626; font-size: 38px; font-style: normal; line-height: 1; text-shadow: 0 1px 2px rgba(255,255,255,.9); transform-origin: center; }
.marking-settings { min-width: 0; border-left: 1px solid #e5e9e6; padding-left: 18px; }
.marking-step { display: flex; align-items: center; gap: 9px; }
.marking-step > span { width: 25px; height: 25px; flex: 0 0 25px; display: grid; place-items: center; border-radius: 50%; background: #eaf2ff; color: #2563eb; font-size: 9px; font-weight: 700; }
.marking-step > div { display: flex; flex-direction: column; gap: 2px; }
.marking-step strong { color: #344139; font-size: 11px; }
.marking-step small { color: #919a94; font-size: 8px; }
.marking-space-name { margin: 10px 0 24px 34px; border-left: 2px solid #2563eb; padding: 7px 10px; background: #f5f8ff; color: #2858b5; font-size: 12px; font-weight: 700; }
.direction-step { margin-bottom: 11px; }
.direction-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 7px; }
.direction-grid button { height: 62px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 5px; border: 1px solid #dce2dd; border-radius: 5px; background: #fff; color: #657069; }
.direction-grid button:hover { border-color: #9ab3e5; }
.direction-grid button.selected { border-color: #2563eb; background: #edf4ff; color: #1d4ed8; box-shadow: inset 0 0 0 1px #2563eb; }
.direction-grid i { display: block; font-size: 21px; font-style: normal; line-height: 1; }
.direction-grid span { font-size: 9px; }
.viewpoint-note { display: flex; flex-direction: column; gap: 7px; margin-top: 17px; }
.viewpoint-note > span { color: #4f5c54; font-size: 10px; font-weight: 600; }
.viewpoint-note em { color: #9aa29d; font-size: 8px; font-style: normal; font-weight: 400; }
.viewpoint-note input { width: 100%; height: 36px; border: 1px solid #dbe1dc; border-radius: 4px; outline: 0; padding: 0 10px; color: #344139; font-size: 10px; }
.viewpoint-note input:focus { border-color: #6692eb; box-shadow: 0 0 0 3px rgba(37,99,235,.1); }
.viewpoint-note textarea { width: 100%; min-height: 70px; resize: vertical; border: 1px solid #dbe1dc; border-radius: 4px; outline: 0; padding: 9px 10px; color: #344139; font-family: inherit; font-size: 10px; line-height: 1.55; }
.viewpoint-note textarea:focus { border-color: #6692eb; box-shadow: 0 0 0 3px rgba(37,99,235,.1); }
.layout-note { margin-top: 14px; }
.refine-reference-note { display: flex; align-items: flex-start; gap: 7px; margin-top: 12px; border-left: 3px solid #2563eb; padding: 9px 10px; background: #f4f7fd; color: #61708a; font-size: 9px; line-height: 1.55; }
.refine-reference-note i { flex: 0 0 auto; margin-top: 1px; color: #2563eb; }
.marking-rule { display: flex; align-items: flex-start; gap: 8px; margin-top: 18px; border-left: 3px solid #2563eb; padding: 10px; background: #f4f7fd; color: #61708a; }
.marking-rule i { flex: 0 0 auto; margin-top: 2px; color: #2563eb; }
.marking-rule p { margin: 0; font-size: 9px; line-height: 1.65; }
.floorplan-marking-dialog .el-button { border-radius: 5px; }
.floorplan-marking-dialog .el-button--primary { border-color: #2563eb; background: #2563eb; }

@media (max-width: 1000px) {
  .floorplan-marking-dialog { width: calc(100% - 30px) !important; margin-top: 4vh !important; }
  .marking-canvas-shell { height: 440px; }
  .marking-image-wrap > img { max-height: 438px; }
}

@media (max-width: 767px) {
  .floorplan-upload-box { min-height: 220px; }
  .constraint-checkbar { align-items: flex-start; flex-direction: column; gap: 8px; margin: 0 17px 10px; padding-top: 13px; }
  .constraint-checkbar b { display: none; }
  .floorplan-marking-dialog { width: calc(100% - 16px) !important; max-height: 94vh; margin-top: 3vh !important; overflow-y: auto; }
  .floorplan-marking-dialog .el-dialog__body { padding: 14px; }
  .marking-layout { grid-template-columns: 1fr; }
  .marking-canvas-shell { height: 330px; }
  .marking-image-wrap > img { max-height: 328px; }
  .marking-settings { border-top: 1px solid #e5e9e6; border-left: 0; padding-top: 16px; padding-left: 0; }
  .marking-space-name { margin-bottom: 18px; }
}
</style>
